"use client";

import { ImageEffectHeading } from "@saas/shared/components/ImageEffectHeading";

import { IMAGE_HERO_EXAMPLE_EVENT } from "@marketing/home/components/HeroControls";
import type { ImageHeroExample } from "@marketing/image-generator/components/ImageGeneratorHero";
import { config } from "@repo/config";
import { trackAiEditImageEvent } from "@saas/ai-edit-image/lib/analytics";
import { useSession } from "@saas/auth/hooks/use-session";
import { CreditsPaywallModal } from "@saas/payments/components/CreditsPaywallModal";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { ImageDetailModal } from "@saas/shared/assets/ImageDetailModal";
import { SelectAssetDialog } from "@saas/shared/assets/SelectAssetDialog";
import { UploadPreviewDialog } from "@saas/shared/assets/UploadPreviewDialog";
import { UploadThumbnailActions } from "@saas/shared/assets/UploadThumbnailActions";
import { VideoDetailModal } from "@saas/shared/assets/VideoDetailModal";
import { AspectRatioSwatch } from "@saas/shared/components/AspectRatioSwatch";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { GenerationSettings } from "@saas/shared/components/GenerationSettings";
import { ModelBrandIcon } from "@saas/shared/components/ModelBrandIcon";
import { ModelMenuItems } from "@saas/shared/components/ModelMenuItems";
import { ModelPicker } from "@saas/shared/components/ModelPicker";
import {
	getAiModelList,
	getAiModelPickerList,
} from "@saas/shared/config/ai-models";
import {
	type FeedView,
	HistoryFeedTabs,
} from "@saas/shared/history/HistoryFeedTabs";
import type { ImageActionLabels } from "@saas/shared/history/HistoryImageActions";
import { WorkbenchTemplates } from "@saas/shared/history/WorkbenchTemplates";
import {
	getImageHomePage,
	useImageHomeRouter,
} from "@saas/shared/history/image-home";
import type {
	HistoryFilter,
	VideoHistoryItem,
} from "@saas/shared/history/types";
import {
	GENERATION_HISTORY_QUERY_KEY,
	IMAGE_HISTORY_PAGE_SIZE,
	groupHistoryEntries,
	historyQueryKey,
} from "@saas/shared/history/types";
import { useResumeHistoryTasks } from "@saas/shared/history/use-resume-history-tasks";
import { useVideoFavorite } from "@saas/shared/history/use-video-favorite";
import { flashMissingField } from "@saas/shared/lib/flash-missing-field";
import { SECTION_TITLE } from "@shared/constants/typography";
import { useRouter } from "@shared/hooks/router";
import { apiClient } from "@shared/lib/api-client";
import { compressImage } from "@shared/lib/image-compression";
import {
	PENDING_GENERATOR_STATE_KEY,
	clearBlobPendingState,
	readBlobPendingState,
} from "@shared/lib/pending-state";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Label } from "@ui/components/label";
import { cn } from "@ui/lib";
import {
	AtSignIcon,
	ChevronDownIcon,
	FolderIcon,
	GemIcon,
	LockIcon,
	UploadIcon,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import {
	getImageModelSettings,
	getMaxInputImages,
} from "../config/model-settings";
import {
	getRootTemplateId,
	getTemplateById,
	getTemplatePath,
} from "../config/templates";
import { useAiImageGenerator } from "../hooks/use-ai-image-generator";
import {
	dropAllImageRefs,
	dropImageRefsAbove,
	firstMissingImageRef,
	formatImageRef,
	removeImageRef,
} from "../lib/image-refs";
import {
	type AIModel,
	type ImageGeneratorApiModel,
	clampResolutionForPlan,
	coerceRetiredModel,
	getImageGenerationCreditCost,
	getResolutionLabel,
	paidModelPaywallReason,
	showsImageResolutionSelector,
	supportsAutoAspectRatio,
	supportsImageResolution,
} from "../types";
import {
	ImageHistoryRow,
	type ImageRowHandlers,
	type ImageRowLabels,
	VideoHistoryRow,
	type VideoRowHandlers,
} from "./HistoryFeedRows";
import { HistoryPreviewShowcaseSkeleton } from "./HistoryPreviewShowcaseSkeleton";
import { ImageRefPicker, type ImageRefPickerHandle } from "./ImageRefPicker";
import { PanelResizer, useLeftPanelWidth } from "./PanelResizer";
import { PromptTextarea, type PromptTextareaHandle } from "./PromptTextarea";
import { VirtualStagingInput } from "./VirtualStagingInput";

// Type for History Item
type ImageGeneration = {
	id: string;
	originalPrompt: string;
	model: string;
	imageUrls: string[];
	inputImageUrls?: string[];
	inputType?: "text" | "image";
	createdAt: string;
	status: "SUCCESS" | "FAILED" | "PENDING";
	/** Indexes into imageUrls that the user starred */
	favoriteImageIndexes?: number[];
	metadata?: {
		timestamp: string;
		/** 出图页面（去掉语言前缀），10/23 起才有 */
		page?: string;
		parameters?: {
			model?: string;
			num_outputs?: number;
			aspect_ratio?: string;
			output_format?: string;
			output_quality?: number;
			num_inference_steps?: number;
			resolution?: string;
		};
		provider?: {
			type: string;
			modelId: string;
			parameters: any;
		};
	};
};

// The history feed is shared with the video generator, so it carries both kinds
// of generation. Video items are handed to VideoHistoryCard as-is.
type HistoryFeedItem = ImageGeneration & { type?: "image" | "video" };

const TemplatePreview = dynamic(
	() => import("./TemplatePreview").then((mod) => mod.TemplatePreview),
	{
		ssr: false,
		loading: () => <div className="flex-1 min-h-0 bg-white" />,
	},
);

const HistoryPreviewShowcase = dynamic(
	() =>
		import("./HistoryPreviewShowcase").then(
			(mod) => mod.HistoryPreviewShowcase,
		),
	{
		ssr: false,
		// 零高占位会让移动端内容弹出时整页下移（CLS），用同尺寸骨架占住位置
		loading: () => <HistoryPreviewShowcaseSkeleton />,
	},
);

const TemplateScrollBar = dynamic(
	() => import("./TemplateScrollBar").then((mod) => mod.TemplateScrollBar),
	{
		ssr: false,
		// 透明：继承右栏底色（新版桌面是灰的），加载完不闪。高度 = 模板条自己的
		// pt-2 + py-2 + 缩略图（96 / sm 112，含 p-0.5）+ pb-4 / sm:pb-8
		loading: () => <div className="h-[140px] sm:h-[172px]" />,
	},
);

const EditUploadedImageDialog = dynamic(
	() =>
		import("@saas/shared/assets/EditUploadedImageDialog").then(
			(mod) => mod.EditUploadedImageDialog,
		),
	{ ssr: false },
);

/** 配置条：和 ImageToVideoClient 的 SETTINGS_BAR_CLASS 同款 */
const SETTINGS_BAR_CLASS =
	"group flex h-12 w-full min-w-0 shrink-0 items-center rounded-xl border border-black/5 bg-zinc-100 px-3 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 data-[state=open]:bg-zinc-200/70";

export function AiImageGeneratorClient({
	titleOverride,
	defaultModel,
	studioLayout = false,
}: {
	titleOverride?: string;
	/** 桌面端照 DeeVid 铺满：去掉外圈留白和两张卡片，左白右灰，中间可拖。
	    2026-10-18 老公定 /ai-edit-image 和 /image-to-image 用；模板页不传，长相不变。
	    页面外包裹要自己抵消 (saas) main 的 lg 内边距 */
	studioLayout?: boolean;
	/** Initial model selection. Falls back to the 6-credit v3.0, which is now
	    the default on every page that mounts this client — ai-image-generator
	    and ai-edit-image both go through the fallback, so no caller passes this
	    today. ai-edit-image used to override it with gpt-image-2 back when
	    returning free users only had 2 credits a day (2026-09-11); that daily
	    grant is gone since 2026-09-14 and the sign-up grant is 15, so the
	    cheap-model override was retired on 2026-09-20. */
	defaultModel?: AIModel;
} = {}) {
	// 页面级默认模型。下面所有「重置回默认」的地方都必须用它而不是硬编码
	// 模型名，否则调用方传入的 defaultModel 会在 effect 里被盖掉。
	const pageDefaultModel = defaultModel ?? "v3.0";
	const t = useTranslations("aiImageGenerator.client");
	const tRoot = useTranslations("aiImageGenerator");
	const tAssets = useTranslations("assets");
	// 下面这些每次渲染都重建的对象都 memo 住（next-intl 的 t 是稳定引用）：
	// 它们直接喂给历史卡片，引用一变卡片就全部重渲染
	const imageActionLabels: ImageActionLabels = useMemo(
		() => ({
			generateVideo: tAssets("detail.generateVideo"),
			download: tAssets("buttons.download"),
			more: tAssets("buttons.more"),
			edit: tAssets("buttons.edit"),
			delete: tAssets("delete"),
			favorite: tAssets("buttons.favorite"),
		}),
		[tAssets],
	);
	const locale = useLocale();
	const { user } = useSession();
	const {
		activePlan,
		canRemoveWatermark,
		loading: purchasesLoading,
	} = usePurchases();
	const queryClient = useQueryClient();
	const { confirm } = useConfirmationAlert();
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const routeImageHome = useImageHomeRouter();

	// Generic template detection from URL - works for any locale-prefixed route
	// Detects if current route is a template route (e.g., /templates/ai-kiss-generator or /zh/templates/ai-kiss-generator)
	// 放在根目录的模板（/ai-muscle-generator、/ai-big-butt）按最后一段认
	const pathSegments = useMemo(
		() => pathname.split("/").filter(Boolean),
		[pathname],
	);
	const templatesIndex = pathSegments.indexOf("templates");
	const templateIdFromUrl =
		templatesIndex !== -1
			? pathSegments[templatesIndex + 1]
			: getRootTemplateId(pathSegments.at(-1));
	const isOnTemplateRoute = Boolean(templateIdFromUrl);

	// Synchronously get template from URL to avoid showing default title flash
	// This ensures the correct template title is shown immediately on initial render
	const validTemplateFromUrl = useMemo(() => {
		if (!templateIdFromUrl) {
			return null;
		}
		return getTemplateById(templateIdFromUrl) ?? null;
	}, [templateIdFromUrl]);

	// Initialize activeTab based on URL - if on template route, default to templates tab
	// 首次渲染就读 ?tab=，服务器直接按它画。以前先画默认的 Image to Image、等 JS 到了
	// 才切，整页打开 ?tab=text-to-image 会先闪错 tab，上传框消失时整块往上跳（10/19 实测手机 CLS 0.14）
	const [activeTab, setActiveTab] = useState<
		"text-to-image" | "image-to-image" | "templates"
	>(() => {
		if (isOnTemplateRoute) {
			return "templates";
		}
		// Templates tab 10/23 下线（模板拆去 /ai-tools 和 /ai-effects），旧链接 ?tab=templates 落回图生图
		return searchParams.get("tab") === "text-to-image"
			? "text-to-image"
			: "image-to-image";
	});

	// Update activeTab when pathname changes (e.g., on page refresh)
	// This ensures that when navigating to a template route, we always show templates tab
	useEffect(() => {
		if (isOnTemplateRoute) {
			setActiveTab("templates");
		}
	}, [isOnTemplateRoute, templateIdFromUrl]);

	// Model list with translations — shared with AI Edit Image via getAiModelList()
	// 不 memo 的话每次渲染都是新数组，下面 modelList 的 useMemo 永远失效
	const allModels = useMemo(() => getAiModelList(t), [t]);

	const {
		prompt,
		setPrompt,
		aspectRatio: size,
		setAspectRatio,
		style,
		setStyle,
		imageCount,
		setImageCount,
		resolution,
		setResolution,
		isPending,
		handleGenerateImage,
		requestLogin,
		generateImageMutation,
		pendingItems,
		addPendingItem,
		resumeImageTasks,
		modelConfigs,
		selectedModel,
		setSelectedModel,
		inputImages,
		setInputImages,
		selectedTemplate,
		setSelectedTemplate,
		clearTemplate,
		currentCreditCost,
		creditCostAfterSwitch,
		getRemainingCreditsWhenLoaded,
		showUpgradeModal,
		setShowUpgradeModal,
		pendingUpgradeReason,
		setPendingUpgradeReason,
		convertImageUrlToBase64,
	} = useAiImageGenerator(activeTab, defaultModel);

	const modelSettings = getImageModelSettings(selectedModel);
	// 图生图参考图上限，默认 5 张；qwen3 上游只收 3 张
	const maxInputImages = getMaxInputImages(selectedModel);
	const aspectRatios = useMemo(
		() =>
			modelSettings.aspectRatios.map((value) => ({
				value,
				label:
					value === "auto"
						? t("imageSettings.aspectRatio.auto")
						: value,
			})),
		[modelSettings, t],
	);

	// 切到不支持 auto 的模型时重置，否则 fal 那边会静默按正方形出图
	useEffect(() => {
		if (size === "auto" && !supportsAutoAspectRatio(selectedModel)) {
			setAspectRatio("1:1");
		}
	}, [selectedModel, size, setAspectRatio]);

	// Track downloading state for individual images
	const [downloadingImages, setDownloadingImages] = useState<Set<string>>(
		new Set(),
	);

	// Optimistic favorite state, keyed by `${generation id}:${image index}` —
	// favoriting is per image, not per generation
	const [favoriteOverrides, setFavoriteOverrides] = useState<
		Record<string, boolean>
	>({});

	const isImageFavorite = (item: ImageGeneration, imageIndex: number) =>
		favoriteOverrides[`${item.id}:${imageIndex}`] ??
		!!item.favoriteImageIndexes?.includes(imageIndex);

	// Clicking a history image opens the same detail modal My Assets uses
	const [selectedImage, setSelectedImage] = useState<{
		generation: ImageGeneration;
		imageIndex: number;
	} | null>(null);
	const { isFavorite: isVideoFavorite, toggleFavorite: toggleVideoFavorite } =
		useVideoFavorite();
	// history 里也混着视频，用同一套详情弹窗
	const [selectedVideo, setSelectedVideo] = useState<VideoHistoryItem | null>(
		null,
	);

	// 图生视频：只把选中的那张图带过去，跟 My Assets 同一条通道。
	// 图片的 prompt 描述的是静态画面，对视频没用
	const handleGenerateVideo = (imageUrl: string) => {
		sessionStorage.setItem(
			"imageToVideoData",
			JSON.stringify({ imageUrl }),
		);
		// Build path with locale prefix if needed (as-needed strategy)
		router.push(
			locale === config.i18n.defaultLocale
				? "/image-to-video?action=image-to-video"
				: `/${locale}/image-to-video?action=image-to-video`,
		);
	};

	// 视频的 reprompt/regenerate 要回填的是视频表单，这一页只有图片表单，
	// 所以带着整条记录跳到视频生成页去做 —— 跟 My Assets 同一条通道
	const handleVideoAction = (
		item: VideoHistoryItem,
		action: "reprompt" | "regenerate",
	) => {
		sessionStorage.setItem("videoActionData", JSON.stringify(item));
		// 老 Grok 工作台 2026-10-06 下线，两条视频线的行都由 /image-to-video 回填
		router.push(
			locale === config.i18n.defaultLocale
				? `/image-to-video?action=${action}`
				: `/${locale}/image-to-video?action=${action}`,
		);
	};

	// 场景卡配图齐全时用场景样片，否则用当前模板已有的轮播示例。
	const templateShowcaseItems = useMemo(() => {
		if (!validTemplateFromUrl) {
			return undefined;
		}
		const useCases = validTemplateFromUrl.marketingContent?.useCases;
		if (!useCases?.length || !useCases.every((uc) => uc.example)) {
			return validTemplateFromUrl.marketingContent?.carouselSlides?.map(
				(slide, index) => ({
					id: `${validTemplateFromUrl.id}-example-${index}`,
					title: `${tRoot(`templates.${validTemplateFromUrl.id}.hero.title`)} · ${index + 1}`,
					referenceImages: slide.referenceImages,
					resultImage: slide.resultImage,
					prompt: slide.prompt ?? validTemplateFromUrl.prompt,
				}),
			);
		}
		return useCases.flatMap((uc, index) =>
			uc.example
				? [
						{
							id: `use-case-${index}`,
							title: tRoot(uc.titleKey),
							referenceImages: [uc.example.before],
							resultImage: uc.example.after,
							prompt:
								uc.example.prompt ??
								validTemplateFromUrl.prompt,
						},
					]
				: [],
		);
	}, [validTemplateFromUrl, tRoot]);

	// Scroll to hero image upload area on small screens
	// Check if screen width is less than md breakpoint (768px)
	const scrollToHeroOnMobile = () => {
		if (typeof window !== "undefined" && window.innerWidth < 768) {
			// Use setTimeout to ensure DOM updates are complete
			setTimeout(() => {
				const heroElement = document.getElementById(
					"ai-image-generator-hero",
				);
				if (heroElement) {
					heroElement.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
				}
			}, 100);
		}
	};

	// 右栏样片 Create 的请求序号：连点时只认最后一次，先点的那组晚到不覆盖后点的（10/28）
	const previewCreateRequestRef = useRef(0);

	// Handle preview showcase template create button click
	const handlePreviewTemplateCreate = async (templateId: string) => {
		// 模板页自己的样片：原图 + prompt 填进模板左栏，不切 tab、不换模型
		const ownExample = templateShowcaseItems?.find(
			(item) => item.id === templateId,
		);
		if (ownExample) {
			if (isUploading) return;
			setIsUploading(true);
			try {
				const images = await Promise.all(
					ownExample.referenceImages.map((url) =>
						url.startsWith("/") ? convertImageUrlToBase64(url) : url,
					),
				);
				setInputImages(images);
				setUploadedImagePreviews(images);
				setPrompt(ownExample.prompt, "templates");
				scrollToHeroOnMobile();
			} catch {
				toast.error("Failed to load template. Please try again.");
			} finally {
				setIsUploading(false);
			}
			return;
		}

		const { previewShowcaseTemplates } = await import(
			"../config/preview-showcase-templates"
		);
		const template = previewShowcaseTemplates.find(
			(t) => t.id === templateId,
		);
		if (!template) return;
		trackAiEditImageEvent(pathname, "ai-edit-image:showcase-clicked", {
			id: templateId,
		});

		const requestId = ++previewCreateRequestRef.current;
		try {
			// Switch to image-to-image tab
			setActiveTab("image-to-image");

			// Set model (if template specifies one, otherwise default to v3.0)
			if (template.model) {
				setSelectedModel(template.model as AIModel);
			} else {
				setSelectedModel("v3.0");
			}

			// Set prompt
			setPrompt(template.prompt, "image-to-image");

			// 图第一次取可能要几十秒（CDN 节点没缓存，回 R2 原站）。先清旧图、上传格转圈，
			// 不然新 prompt 配着旧图、看着像点了没反应（10/28 老公在线上碰到）
			setInputImages([]);
			setUploadedImagePreviews([]);
			setIsUploading(true);

			// Convert reference images to base64 and set input images
			const base64Images = await Promise.all(
				template.referenceImages.map((url) =>
					convertImageUrlToBase64(url),
				),
			);
			if (requestId !== previewCreateRequestRef.current) return;
			setInputImages(base64Images);
			setUploadedImagePreviews(base64Images);
			scrollToHeroOnMobile();
		} catch (error) {
			if (requestId !== previewCreateRequestRef.current) return;
			console.error("Failed to load template:", error);
			toast.error("Failed to load template. Please try again.");
		} finally {
			if (requestId === previewCreateRequestRef.current) setIsUploading(false);
		}
	};

	// Filter models based on template's allowed models (if specified) and active tab
	const GUEST_ALLOWED_MODELS: AIModel[] = [
		"v1.0",
		"v2.0",
		"v3.0",
		"gpt-image-2",
	];
	const isModelGuestLocked = (model: AIModel) =>
		!user && !GUEST_ALLOWED_MODELS.includes(model);
	const modelList = useMemo(() => {
		// 去掉 hidden 的；模板点名 allowed 时只列 allowed（hidden 的也照列）
		let filtered = getAiModelPickerList(
			allModels,
			selectedTemplate?.behavior?.model?.allowed,
		);

		// Filter out v1.0 in image-to-image mode (v1.0 only supports text-to-image)
		if (activeTab === "image-to-image") {
			filtered = filtered.filter((model) => model.id !== "v1.0");
		}

		return filtered;
	}, [allModels, selectedTemplate, activeTab]);

	const resolutionOptions = useMemo(() => {
		return (
			selectedTemplate?.behavior?.resolution?.allowed ??
			modelSettings.resolutions
		);
	}, [selectedTemplate, modelSettings]);
	const showResolutionSelector =
		showsImageResolutionSelector(selectedModel) &&
		!validTemplateFromUrl?.behavior?.hideResolution;

	// 模板 tab 的设置行有三个下拉框（比例 / 清晰度 / 张数），每个都可能被模板
	// 或模型隐藏，所以栅格列数得按实际可见数量算，不能写死。
	const templateSettingCount =
		(validTemplateFromUrl?.behavior?.hideAspectRatio ? 0 : 1) +
		(showResolutionSelector ? 1 : 0) +
		(validTemplateFromUrl?.behavior?.hideOutputCount ? 0 : 1);

	// 高清档只给付费用户。锁哪几档直接读 creditCostByResolution 的 key——那张表
	// 就是「这一档更贵」的唯一真相，跟后端 CREDIT_COST_OVERRIDES 是同一份数据。
	// 于是 v3.0（三档同价）一档不锁，nano-banana-pro 只锁 4K 而不锁 2K。
	// purchasesLoading 期间不锁，避免刚进页面闪一下锁图标。
	const lockedResolutions = useMemo(() => {
		if (purchasesLoading || canRemoveWatermark) {
			return new Set<string>();
		}
		return new Set(
			Object.keys(
				modelConfigs?.[selectedModel]?.creditCostByResolution ?? {},
			),
		);
	}, [purchasesLoading, canRemoveWatermark, modelConfigs, selectedModel]);

	// 兜底：模板默认值或存储恢复的档位可能落在锁定区，UI 显示 2K 而后端实际跑
	// 1K，历史记录里对不上。发现就拉回 1K。
	useEffect(() => {
		if (lockedResolutions.has(resolution)) {
			setResolution("1K");
		}
	}, [lockedResolutions, resolution, setResolution]);

	// 重新生成走的是历史记录里的档位，不经过上面那个 effect 监听的组件 state，
	// 所以要在发请求前单独算一次有效档位。purchasesLoading 期间按付费算，跟
	// lockedResolutions 保持同一套语义，剩下的交给后端兜。
	const effectiveResolution = useCallback(
		<T extends string>(model: AIModel, resolution: T) =>
			clampResolutionForPlan(
				model,
				resolution,
				purchasesLoading || canRemoveWatermark,
				modelConfigs,
			),
		[purchasesLoading, canRemoveWatermark, modelConfigs],
	);

	// 整个模型只给付费用户的（nano-banana-pro / qwen3 pro / seedream 5 pro）。锁哪些模型读 /models
	// 下发的 paidOnly，跟后端 PAID_ONLY_MODELS 是同一份数据，前端不自己列名单。
	// 与分辨率锁同样在 purchasesLoading 期间不锁，避免进页面闪一下。
	const isModelPaidLocked = useCallback(
		(model: AIModel) =>
			!purchasesLoading &&
			!canRemoveWatermark &&
			!!modelConfigs?.[model]?.paidOnly,
		[purchasesLoading, canRemoveWatermark, modelConfigs],
	);

	// 兜底：模板、URL query、localStorage 恢复的模型都可能是付费专属的，UI 显示
	// pro 而后端直接 403。发现就拉回默认模型。
	useEffect(() => {
		if (isModelPaidLocked(selectedModel)) {
			setSelectedModel(pageDefaultModel);
		}
	}, [isModelPaidLocked, selectedModel, setSelectedModel, pageDefaultModel]);

	const setSize = setAspectRatio;

	// 模板页不出 tab 栏，这里只会在图生图 / 文生图之间切
	const handleTabChange = (tab: "text-to-image" | "image-to-image") => {
		// Apply default model when switching tabs. 免费/付费、文生图/图生图都用
		// 页面默认模型。设的值必须是模型下拉里还在的那些，否则 ModelPicker
		// 的 `?? models[0]` 会兜底显示成别的，而请求里发的仍是这个——静默错配。
		setSelectedModel(pageDefaultModel);

		setActiveTab(tab);
		clearTemplate();
	};

	// Filtering has to happen on the server: the feed is infinite-scrolled, so
	// dropping items after pagination would make "no more items" lie.
	const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
	// null = 用户还没点过右栏的 tab，按有没有作品自动选（见 activeView）
	const [feedView, setFeedView] = useState<FeedView | null>(null);

	const {
		data: historyData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
	} = useInfiniteQuery({
		queryKey: historyQueryKey(IMAGE_HISTORY_PAGE_SIZE, historyFilter),
		enabled: Boolean(user),
		queryFn: async ({ pageParam }) => {
			const response = await apiClient.generations.history.$get({
				query: {
					page: pageParam.toString(),
					limit: String(IMAGE_HISTORY_PAGE_SIZE),
					filter: historyFilter,
				},
			});
			if (!response.ok) {
				throw new Error("Failed to fetch history");
			}
			return await response.json();
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			if (lastPage.pagination.page < lastPage.pagination.totalPages) {
				return lastPage.pagination.page + 1;
			}
			return undefined;
		},
	});

	// 生成中的占位卡片不进 query 缓存——那份缓存会被 refetch 整包替换，占位卡就没了。
	// 它只在渲染时拼到列表最前面，出图后由 hook 统一撤掉。
	// kie 模型提交后库里马上有一行 PENDING，占位卡还在时把库里在途的图片行藏掉，
	// 免得同一单显示两张卡（同时只会有一单在跑）。
	const historyItems = useMemo(() => {
		const rows = historyData?.pages.flatMap((page: any) => page.data) ?? [];
		if (historyFilter === "video" || pendingItems.length === 0) {
			return [
				...(historyFilter === "video" ? [] : pendingItems),
				...rows,
			];
		}
		return [
			...pendingItems,
			...rows.filter(
				(row: any) =>
					!(
						row.type === "image" &&
						(row.status === "PENDING" ||
							row.status === "PROCESSING")
					),
			),
		];
	}, [pendingItems, historyData, historyFilter]);

	// 访客只有示例和模板。登录用户点过 tab 就听用户的；没点过时有作品看作品、没作品看示例，
	// 历史还在加载就先按作品转圈，免得示例先出来再被换掉
	const activeView: FeedView = !user
		? feedView === "templates"
			? "templates"
			: "examples"
		: (feedView ??
			(historyItems.length > 0 || isLoading ? "mine" : "examples"));

	// 新增一张生成中的占位卡就切到我的作品，停在示例 tab 上看不到进度。
	// 只看增加：出完图占位卡撤掉时别把正在看示例的人拽走
	const pendingCount = pendingItems.length;
	const prevPendingCountRef = useRef(pendingCount);
	useEffect(() => {
		if (pendingCount > prevPendingCountRef.current) {
			setFeedView("mine");
		}
		prevPendingCountRef.current = pendingCount;
	}, [pendingCount]);

	// 刷新或关页再回来时，库里还在跑的图片任务没人在查，接着查到出结果
	useEffect(() => {
		resumeImageTasks(
			historyData?.pages.flatMap((page: any) => page.data) ?? [],
		);
	}, [historyData, resumeImageTasks]);
	// 在视频页出完片切过来的，History 里还在转的视频也接着查
	useResumeHistoryTasks(
		historyData?.pages.flatMap((page: any) => page.data) ?? [],
		"video",
	);
	const historyEntries = useMemo(
		() => groupHistoryEntries(historyItems),
		[historyItems],
	);

	// A new generation is written into the All feed's cache as an optimistic
	// pending card, so jump back to that tab instead of leaving the user on a
	// filtered view where nothing seems to happen.
	useEffect(() => {
		if (isPending) {
			setHistoryFilter("all");
		}
	}, [isPending]);

	const loadMoreRef = useRef<HTMLDivElement>(null);
	const { ref: inViewRef, inView } = useInView();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const historyScrollRef = useRef<HTMLDivElement>(null);
	const { width: leftPanelWidth, commit: commitLeftPanelWidth } =
		useLeftPanelWidth();
	const formScrollRef = useRef<HTMLDivElement>(null);

	// Reset scroll position on mount to prevent browser scroll restoration flash
	useEffect(() => {
		if (formScrollRef.current) {
			formScrollRef.current.scrollTop = 0;
		}
	}, []);

	const [isDragOver, setIsDragOver] = useState(false);
	const [isAssetDialogOpen, setIsAssetDialogOpen] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadedImagePreviews, setUploadedImagePreviews] = useState<
		string[]
	>([]);
	const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
	const [editImageIndex, setEditImageIndex] = useState<number | null>(null);
	// Prompt 里的 @ Reference（10/21 轻量版）：弹 My Assets，选图后插「Image N」
	const [isRefPickerOpen, setIsRefPickerOpen] = useState(false);
	// 光标停在哪个「Image N」上，上传区那张描边
	const [activeImageRef, setActiveImageRef] = useState<number | null>(null);
	const promptHandleRef = useRef<PromptTextareaHandle>(null);
	const refPickerHandleRef = useRef<ImageRefPickerHandle>(null);
	const refButtonRef = useRef<HTMLButtonElement>(null);
	const promptBoxRef = useRef<HTMLDivElement>(null);
	// 从 @ 弹层点 Browse all 打开的素材弹框，选完要把编号插进 prompt
	const browseInsertsRefsRef = useRef(false);
	const [regeneratingItemId, setRegeneratingItemId] = useState<string | null>(
		null,
	);
	const [hasRestoredState, setHasRestoredState] = useState(false);
	const [hasAppliedUrlParams, setHasAppliedUrlParams] = useState(false);
	const previousHistoryLengthRef = useRef<number>(0);

	// A new generation always lands at the top of the history, so bring it into
	// view. On small screens the history sits below the form and needs the page
	// scrolled to it first; on desktop it's a side panel that is already visible.
	useEffect(() => {
		if (!historyData || !historyData.pages || !historyData.pages[0]) {
			return;
		}

		const currentHistoryLength = historyData.pages[0].data?.length || 0;
		const hasNewItem =
			currentHistoryLength > previousHistoryLengthRef.current;

		if (
			hasNewItem &&
			previousHistoryLengthRef.current > 0 // Not initial load
		) {
			// Wait for DOM to update
			setTimeout(() => {
				if (!historyScrollRef.current) {
					return;
				}

				if (window.innerWidth < 768) {
					historyScrollRef.current.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
				}

				setTimeout(() => {
					historyScrollRef.current?.scrollTo({
						top: 0,
						behavior: "smooth",
					});
				}, 300);
			}, 100);
		}

		previousHistoryLengthRef.current = currentHistoryLength;
	}, [historyData]);
	// Track the reason for showing upgrade modal
	const [upgradeModalReason, setUpgradeModalReason] = useState<
		| "nano-banana-pro"
		| "paid-model"
		| "insufficient-credits"
		| "watermark"
		| "image-resolution"
		| "post-generation"
		| null
	>(null);
	// 模型卡的下拉 / 手机底部面板（ModelPicker）
	const [modelMenuOpen, setModelMenuOpen] = useState(false);
	// Track selected template for preview showcase
	// Initialize with first template to avoid random selection on refresh
	const [selectedPreviewTemplateId, setSelectedPreviewTemplateId] = useState<
		string | undefined
	>();

	// Apply aspect ratio and model from URL parameters immediately (highest priority)
	// This runs on every searchParams change to ensure we catch the value
	useEffect(() => {
		if (hasAppliedUrlParams) return;

		const ratioFromQuery = searchParams.get("aspect_ratio");
		const sourceFromQuery = searchParams.get("source");
		if (ratioFromQuery) {
			console.log(
				"[ai-image-generator] Applying aspectRatio from URL:",
				ratioFromQuery,
			);
			setAspectRatio(ratioFromQuery);
		}

		const modelFromQuery = searchParams.get("model");
		if (modelFromQuery && modelList.find((m) => m.id === modelFromQuery)) {
			console.log(
				"[ai-image-generator] Applying model from URL:",
				modelFromQuery,
			);
			setSelectedModel(modelFromQuery as AIModel);
		}

		// Allow deep-linking to a specific tab (used when leaving template routes).
		// Do not override template routes.
		const tabFromQuery = searchParams.get("tab");
		if (!isOnTemplateRoute && tabFromQuery) {
			if (
				tabFromQuery === "image-to-image" ||
				tabFromQuery === "text-to-image"
			) {
				setActiveTab(tabFromQuery);
			}
		}

		// Match the Age Filter default when a deep link omits a valid model.
		if (
			sourceFromQuery === "age-filter" &&
			(!modelFromQuery || !modelList.find((m) => m.id === modelFromQuery))
		) {
			console.log(
				"[ai-image-generator] Applying model from age-filter source",
			);
			setSelectedModel("nano-banana-2-1");
		}

		if (
			ratioFromQuery ||
			modelFromQuery ||
			sourceFromQuery === "age-filter" ||
			tabFromQuery
		) {
			setHasAppliedUrlParams(true);
		}
	}, [
		searchParams,
		hasAppliedUrlParams,
		isOnTemplateRoute,
		setAspectRatio,
		setSelectedModel,
	]);

	// Generic template auto-selection from URL
	// When on a template route, auto-select the template and switch to templates tab
	useEffect(() => {
		if (validTemplateFromUrl) {
			// Always set the template when on template route, even if already selected
			// This ensures it works on page refresh
			if (
				!selectedTemplate ||
				selectedTemplate.id !== validTemplateFromUrl.id
			) {
				console.log(
					"[AiImageGeneratorClient] Auto-selecting template from URL:",
					validTemplateFromUrl.id,
				);
				// Call setSelectedTemplate (which is handleSelectTemplate) to load example images
				setSelectedTemplate(validTemplateFromUrl);
			}
			// Ensure we're on templates tab when on template route
			if (activeTab !== "templates") {
				setActiveTab("templates");
			}
		}
	}, [
		validTemplateFromUrl,
		selectedTemplate,
		setSelectedTemplate,
		activeTab,
	]);

	// Sync uploadedImagePreviews with inputImages for templates
	// This ensures that when template example images are loaded into inputImages,
	// they are also shown in the UI via uploadedImagePreviews
	useEffect(() => {
		if (
			activeTab === "templates" &&
			selectedTemplate &&
			inputImages.length > 0
		) {
			// Only update if uploadedImagePreviews is different from inputImages.
			// 逐项比较而不是 JSON.stringify：数组里可能是几 MB 的 base64 data URL
			const isSame =
				uploadedImagePreviews.length === inputImages.length &&
				uploadedImagePreviews.every((url, i) => url === inputImages[i]);
			if (!isSame) {
				console.log(
					"[AiImageGeneratorClient] Syncing uploadedImagePreviews with inputImages for template:",
					selectedTemplate.id,
				);
				setUploadedImagePreviews(inputImages);
			}
		}
	}, [activeTab, selectedTemplate, inputImages, uploadedImagePreviews]);

	// 切到参考图上限更小的模型（qwen3 只收 3 张）时截掉多出来的并提示，
	// 否则后端会静默只用前几张
	useEffect(() => {
		if (
			inputImages.length > maxInputImages ||
			uploadedImagePreviews.length > maxInputImages
		) {
			setInputImages(inputImages.slice(0, maxInputImages));
			setUploadedImagePreviews(
				uploadedImagePreviews.slice(0, maxInputImages),
			);
			// 被砍掉的图在 prompt 里的编号一起删，不然模型会去找不存在的 Image 4
			setPrompt(dropImageRefsAbove(prompt, maxInputImages));
			toast.info(
				t("imageSettings.upload.trimmedToModelLimit", {
					max: maxInputImages,
				}),
			);
		}
	}, [
		inputImages,
		uploadedImagePreviews,
		maxInputImages,
		setInputImages,
		prompt,
		setPrompt,
		t,
	]);

	// Restore state from IndexedDB after login
	useEffect(() => {
		// Only restore once when component mounts and user is logged in
		if (hasRestoredState) return;

		// 如果用户未登录，不设置 hasRestoredState，等待用户登录后再尝试恢复
		if (!user) return;

		void (async () => {
			// readBlobPendingState 已经处理了过期（1 小时），过期的读出来就是 null
			const state = await readBlobPendingState<Record<string, any>>(
				PENDING_GENERATOR_STATE_KEY,
				{ maxAgeMs: 3600000 },
			);
			if (!state) {
				setHasRestoredState(true);
				return;
			}

			try {
				// Restore all state
				console.log("Restoring generator state:", state);

				// Check if this is from any image transformation source
				const isFromImageTransformation = [
					"photo-to-pixar",
					"photo-to-anime",
					"age-filter",
					"ai-buzz-cut",
					"ai-nano-banana",
				].includes(state.source);
				const hasInputImages =
					Array.isArray(state.inputImages) &&
					state.inputImages.length > 0;
				const restoredTab =
					state.activeTab === "image-to-image" ||
					(isFromImageTransformation && hasInputImages)
						? "image-to-image"
						: "text-to-image";

				if (state.prompt) {
					setPrompt(state.prompt, restoredTab);
				}
				// Only restore selectedModel from localStorage if URL param was not applied
				if (!hasAppliedUrlParams && state.selectedModel) {
					// Coerce legacy/removed model values to the current default.
					// v1.0 / v2.0 都已从模型下拉下架，恢复它们会让 ModelPicker 的
					// `?? models[0]` 兜底显示成 V3.0、请求里却发旧模型。
					const restoredModel =
						state.selectedModel === "v1.0" ||
						state.selectedModel === "v2.0"
							? pageDefaultModel
							: coerceRetiredModel(state.selectedModel);
					setSelectedModel(restoredModel);
				}
				// Only restore aspectRatio from localStorage if URL param was not applied
				if (!hasAppliedUrlParams && state.aspectRatio) {
					console.log(
						"[ai-image-generator] Restoring aspectRatio from localStorage:",
						state.aspectRatio,
					);
					setAspectRatio(state.aspectRatio);
				}
				if (state.imageCount) {
					setImageCount(state.imageCount);
				}
				if (state.resolution) {
					setResolution(state.resolution);
				}
				if (state.style) {
					setStyle(state.style);
				}
				// Ensure image-to-image tab is selected for transformation sources that include input images
				// BUT: If we're on a template route, don't override the templates tab
				// Priority: template route > saved state > transformation source
				if (isOnTemplateRoute) {
					// On template route, always stay on templates tab (highest priority)
					setActiveTab("templates");
				} else if (
					state.activeTab === "image-to-image" ||
					(isFromImageTransformation && hasInputImages)
				) {
					setActiveTab("image-to-image");
				} else if (state.activeTab === "text-to-image") {
					// 老现场可能存着已下线的 templates tab，只认文生图
					setActiveTab("text-to-image");
				}
				// Restore images (Base64)
				if (state.inputImages && Array.isArray(state.inputImages)) {
					setInputImages(state.inputImages);
					setUploadedImagePreviews(state.inputImages);
				}

				// Mark as restored to prevent re-entry within this component instance
				setHasRestoredState(true);

				// Delay clearing to handle component remount after email login.
				// Email login triggers router.replace which may remount the component;
				// keeping the record around a moment means the remount still finds it.
				setTimeout(() => {
					void clearBlobPendingState(PENDING_GENERATOR_STATE_KEY);
				}, 2000);

				// Intentionally no toast on restore
			} catch (error) {
				console.error("Error restoring generator state:", error);
				// Clear corrupted state
				void clearBlobPendingState(PENDING_GENERATOR_STATE_KEY);
			}
		})();
	}, [
		user,
		hasRestoredState,
		hasAppliedUrlParams,
		isOnTemplateRoute,
		setPrompt,
		setSelectedModel,
		setAspectRatio,
		setImageCount,
		setResolution,
		setStyle,
		setInputImages,
	]);

	// Track the last tab and plan for which we applied default models to avoid "locking" manual selections
	const lastAppliedDefaultRef = useRef<{
		tab: string;
		planId: string | undefined;
	} | null>(null);

	// Initialize default model based on user plan and tab if not already set by URL/Storage
	useEffect(() => {
		// Only run if we haven't applied custom model from URL or restored from Storage
		// and we are not on a specific template route (which has its own model logic)
		if (hasAppliedUrlParams || hasRestoredState || isOnTemplateRoute)
			return;

		// Wait for purchases to finish loading to know the definitive plan
		if (purchasesLoading) return;

		const currentTab = activeTab;
		const currentPlanId = activePlan?.id;

		// If we already applied the default for this specific combination of tab and plan,
		// don't do it again to allow for manual user selection.
		if (
			lastAppliedDefaultRef.current?.tab === currentTab &&
			lastAppliedDefaultRef.current?.planId === currentPlanId
		) {
			return;
		}

		const isFreeUser = !canRemoveWatermark;

		// 免费/付费、两个标签都用页面默认模型。别再按 plan 分叉：设成已从
		// 模型下拉下架的 id 会让 ModelPicker 的 `?? models[0]` 兜底显示成别的，而
		// 请求里发的仍是那个 id——用户看到的和实际跑的不是一个模型。
		if (isFreeUser || activePlan) {
			setSelectedModel(pageDefaultModel);
		}

		// Mark this combination as handled
		lastAppliedDefaultRef.current = {
			tab: currentTab,
			planId: currentPlanId,
		};
	}, [
		activePlan,
		canRemoveWatermark,
		purchasesLoading,
		activeTab,
		hasAppliedUrlParams,
		hasRestoredState,
		isOnTemplateRoute,
		setSelectedModel,
		pageDefaultModel,
	]);

	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, fetchNextPage]);

	// Set reason for upgrade modal when opened from hook. hook 会在付费专属模型
	// 被拒时带上 pendingUpgradeReason，没带就还是老的「积分不足」。
	useEffect(() => {
		if (showUpgradeModal && !upgradeModalReason) {
			setUpgradeModalReason(
				pendingUpgradeReason ?? "insufficient-credits",
			);
		}
	}, [showUpgradeModal, upgradeModalReason, pendingUpgradeReason]);

	// Prevent page scroll when history panel is scrolled to top or bottom
	useEffect(() => {
		const historyScrollElement = historyScrollRef.current;
		if (!historyScrollElement) return;

		const handleWheel = (e: WheelEvent) => {
			const { scrollTop, scrollHeight, clientHeight } =
				historyScrollElement;
			// 移动端面板不限高，clientHeight === scrollHeight，此时它既算"在顶部"
			// 又算"在底部"，下面两条会把两个方向的滚动全吃掉——面板自己根本不滚，
			// 没什么可拦的，直接放行让页面滚
			if (scrollHeight <= clientHeight) {
				return;
			}
			const isAtTop = scrollTop === 0;
			const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 for rounding errors

			// If scrolling up and at top, prevent default
			if (isAtTop && e.deltaY < 0) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}

			// If scrolling down and at bottom, prevent default
			if (isAtBottom && e.deltaY > 0) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}
		};

		historyScrollElement.addEventListener("wheel", handleWheel, {
			passive: false,
			capture: true,
		});

		return () => {
			historyScrollElement.removeEventListener("wheel", handleWheel);
		};
	}, []);

	// Auto-adjust resolution when switching to v3.0 (no longer needed as v3.0 supports 1K now)
	// useEffect(() => {
	// 	if (selectedModel === "v3.0" && resolution === "1K") {
	// 		setResolution("2K");
	// 	}
	// }, [selectedModel, resolution, setResolution]);

	// "original" 已被 auto 取代，但 IndexedDB 的待恢复状态、历史记录的重新生成、
	// 模板配置里都可能还带着这个值。统一在这里迁移：模板有默认比例就用它，否则
	// 用 auto（模型支持的话），再否则回落 1:1。
	// 这一条取代了原来三个分别处理 text-to-image / image-to-image / 模板的 effect。
	useEffect(() => {
		if (size !== "original") {
			return;
		}
		setAspectRatio(
			validTemplateFromUrl?.behavior?.defaultAspectRatio ??
				(supportsAutoAspectRatio(selectedModel) ? "auto" : "1:1"),
		);
	}, [size, selectedModel, validTemplateFromUrl, setAspectRatio]);

	// 比例跟随输入图（behavior.aspectRatioFollowsInput）：按第一张图的宽高比就近吸附
	const followsInputRatio =
		validTemplateFromUrl?.behavior?.aspectRatioFollowsInput === true;
	const firstInputImage = inputImages[0];
	const sizeRef = useRef(size);
	sizeRef.current = size;
	useEffect(() => {
		if (!followsInputRatio || !firstInputImage) {
			return;
		}
		// 图加载期间比例被别处改过（登录后恢复的设置、用户手选），就不再覆盖
		const sizeAtStart = sizeRef.current;
		let cancelled = false;
		const img = new window.Image();
		img.onload = () => {
			if (
				cancelled ||
				sizeRef.current !== sizeAtStart ||
				!img.naturalWidth ||
				!img.naturalHeight
			) {
				return;
			}
			const target = Math.log(img.naturalWidth / img.naturalHeight);
			let best = "";
			let bestDistance = Number.POSITIVE_INFINITY;
			for (const ratio of modelSettings.aspectRatios) {
				const [w, h] = ratio.split(":").map(Number);
				if (!w || !h) {
					continue;
				}
				const distance = Math.abs(Math.log(w / h) - target);
				if (distance < bestDistance) {
					best = ratio;
					bestDistance = distance;
				}
			}
			if (best) {
				setAspectRatio(best);
			}
		};
		img.src = firstInputImage;
		return () => {
			cancelled = true;
		};
	}, [followsInputRatio, firstInputImage, modelSettings, setAspectRatio]);

	// Handle reprompt/regenerate actions from my-assets page via sessionStorage
	useEffect(() => {
		const action = searchParams.get("action");
		if (!action) return;
		// 带着作品回到模板页（image-home.ts）：等模板选好再填。选模板会把 prompt 重置成模板
		// 默认的，dev 的 StrictMode 头一回挂载还会选两次，先填就被盖掉
		if (isOnTemplateRoute && selectedTemplate?.id !== templateIdFromUrl) {
			return;
		}

		// Handle reprompt action
		if (action === "reprompt") {
			try {
				const repromptDataStr = sessionStorage.getItem("repromptData");
				if (repromptDataStr) {
					const repromptData = JSON.parse(repromptDataStr);
					console.log(
						"[ai-image-generator] Processing reprompt action:",
						repromptData,
					);

					// Set prompt
					if (repromptData.prompt) {
						const repromptTab =
							repromptData.inputType === "image" &&
							repromptData.inputImageUrls?.length > 0
								? "image-to-image"
								: "text-to-image";
						// 从别的页回到姿势页这类模板页（image-home.ts）：填模板那一栏，不切 tab
						setPrompt(
							repromptData.prompt,
							isOnTemplateRoute ? "templates" : repromptTab,
						);
					}

					// Set tab and images based on input type
					if (
						repromptData.inputType === "image" &&
						repromptData.inputImageUrls?.length > 0
					) {
						if (!isOnTemplateRoute) {
							setActiveTab("image-to-image");
						}
						setInputImages(repromptData.inputImageUrls);
						setUploadedImagePreviews(repromptData.inputImageUrls);
					} else if (!isOnTemplateRoute) {
						setActiveTab("text-to-image");
					} else {
						// 没传图的记录回到可选传图的模板页（脚图页）：清掉恢复出来的旧照片
						setInputImages([]);
						setUploadedImagePreviews([]);
					}

					// Scroll to left panel on small screens where layout is vertical
					setTimeout(() => {
						if (window.innerWidth < 768) {
							const leftPanel = document.getElementById(
								"ai-image-generator-left-panel",
							);
							if (leftPanel) {
								leftPanel.scrollIntoView({
									behavior: "smooth",
									block: "start",
								});
							}
						}
					}, 200);

					// Clear sessionStorage
					sessionStorage.removeItem("repromptData");

					// Clear URL parameter
					const url = new URL(window.location.href);
					url.searchParams.delete("action");
					window.history.replaceState({}, "", url.toString());
				}
			} catch (error) {
				console.error(
					"[ai-image-generator] Error processing reprompt action:",
					error,
				);
				sessionStorage.removeItem("repromptData");
			}
		}

		// Handle regenerate action
		if (action === "regenerate") {
			try {
				const regenerateDataStr =
					sessionStorage.getItem("regenerateData");
				if (regenerateDataStr) {
					const regenerateData = JSON.parse(regenerateDataStr);
					console.log(
						"[ai-image-generator] Processing regenerate action:",
						regenerateData,
					);

					if (regenerateData.generation) {
						const generation = regenerateData.generation;

						// Set prompt
						if (generation.originalPrompt) {
							const regenerateTab =
								generation.inputImageUrls?.length > 0
									? "image-to-image"
									: "text-to-image";
							// 同 reprompt：回到模板页填模板那一栏，不切 tab
							setPrompt(
								generation.originalPrompt,
								isOnTemplateRoute ? "templates" : regenerateTab,
							);
						}

						// Set tab and images based on input type
						if (generation.inputImageUrls?.length > 0) {
							if (!isOnTemplateRoute) {
								setActiveTab("image-to-image");
							}
							setInputImages(generation.inputImageUrls);
							setUploadedImagePreviews(generation.inputImageUrls);
						} else if (!isOnTemplateRoute) {
							setActiveTab("text-to-image");
						} else {
							setInputImages([]);
							setUploadedImagePreviews([]);
						}

						// Scroll to left panel on small screens where layout is vertical
						setTimeout(() => {
							if (window.innerWidth < 768) {
								const leftPanel = document.getElementById(
									"ai-image-generator-left-panel",
								);
								if (leftPanel) {
									leftPanel.scrollIntoView({
										behavior: "smooth",
										block: "start",
									});
								}
							}
						}, 200);

						// Trigger regenerate after populating the form
						setTimeout(() => {
							// Build a proper ImageGeneration object
							const historyItem: ImageGeneration = {
								id: generation.id || `regenerate-${Date.now()}`,
								originalPrompt: generation.originalPrompt,
								model: generation.model,
								imageUrls: generation.imageUrls || [],
								inputImageUrls: generation.inputImageUrls,
								inputType:
									generation.inputImageUrls?.length > 0
										? "image"
										: "text",
								createdAt:
									generation.createdAt ||
									new Date().toISOString(),
								status: "SUCCESS",
								metadata: generation.metadata,
							};

							// Trigger regenerate by calling the mutation directly
							const params =
								historyItem.metadata?.parameters || {};
							const model = (params.model ||
								historyItem.model) as AIModel;
							const aspectRatioVal = params.aspect_ratio || "1:1";
							const numOutputs = params.num_outputs || 1;

							let resolutionVal: "1K" | "2K" | "4K" = "1K";
							if (params.resolution) {
								resolutionVal = params.resolution as
									| "1K"
									| "2K"
									| "4K";
							}
							// 同 handleRegenerate：免费用户的高清档在这里就降下来，
							// 别让乐观记录先显示 4K 再被后端改成 1K。
							resolutionVal = effectiveResolution(
								model,
								resolutionVal,
							);

							const inputTypeVal: "text" | "image" =
								historyItem.inputType === "image" ||
								(historyItem.inputImageUrls?.length ?? 0) > 0
									? "image"
									: "text";

							const requestData: any = {
								prompt: historyItem.originalPrompt,
								model,
								aspect_ratio: aspectRatioVal,
								num_outputs: numOutputs,
								isPublic: false,
								inputType: inputTypeVal,
							};

							if (
								inputTypeVal === "image" &&
								historyItem.inputImageUrls?.length
							) {
								requestData.image_input =
									historyItem.inputImageUrls;
							}

							if (supportsImageResolution(model)) {
								requestData.resolution = resolutionVal;
							}

							// Create optimistic pending item for regenerate
							addPendingItem({
								id: `pending-${Date.now()}`,
								originalPrompt: requestData.prompt,
								model: requestData.model,
								imageUrls: [],
								inputImageUrls:
									inputTypeVal === "image"
										? historyItem.inputImageUrls
										: undefined,
								inputType: inputTypeVal,
								createdAt: new Date().toISOString(),
								status: "PENDING",
								parameters: {
									num_outputs: numOutputs,
								},
								metadata: {
									timestamp: new Date().toISOString(),
									parameters: {
										model,
										aspect_ratio: aspectRatioVal,
										num_outputs: numOutputs,
										resolution: resolutionVal,
									},
								},
							});

							generateImageMutation.mutate(requestData);
						}, 300);
					}

					// Clear sessionStorage
					sessionStorage.removeItem("regenerateData");

					// Clear URL parameter
					const url = new URL(window.location.href);
					url.searchParams.delete("action");
					window.history.replaceState({}, "", url.toString());
				}
			} catch (error) {
				console.error(
					"[ai-image-generator] Error processing regenerate action:",
					error,
				);
				sessionStorage.removeItem("regenerateData");
			}
		}

		// Handle edit action (from template pages)
		if (action === "edit") {
			try {
				const editDataStr = sessionStorage.getItem("editImageData");
				if (editDataStr) {
					const editData = JSON.parse(editDataStr);
					console.log(
						"[ai-image-generator] Processing edit action:",
						editData,
					);

					// Set tab to image-to-image and load the image
					if (editData.imageUrl) {
						setActiveTab("image-to-image");
						// Set default model when editing
						setSelectedModel(pageDefaultModel);
						// Clear prompt text area for fresh input
						setPrompt("", "image-to-image");
						setInputImages([editData.imageUrl]);
						setUploadedImagePreviews([editData.imageUrl]);
					}

					// Scroll to left panel on small screens where layout is vertical
					setTimeout(() => {
						if (window.innerWidth < 768) {
							const leftPanel = document.getElementById(
								"ai-image-generator-left-panel",
							);
							if (leftPanel) {
								leftPanel.scrollIntoView({
									behavior: "smooth",
									block: "start",
								});
							}
						}
					}, 200);

					// Clear sessionStorage
					sessionStorage.removeItem("editImageData");

					// Clear URL parameter
					const url = new URL(window.location.href);
					url.searchParams.delete("action");
					window.history.replaceState({}, "", url.toString());
				}
			} catch (error) {
				console.error(
					"[ai-image-generator] Error processing edit action:",
					error,
				);
				sessionStorage.removeItem("editImageData");
			}
		}
	}, [
		searchParams,
		setPrompt,
		setActiveTab,
		setInputImages,
		generateImageMutation,
		isOnTemplateRoute,
		selectedTemplate?.id,
		templateIdFromUrl,
	]);

	// 模板页营销区左右交替样片的「Try this prompt」（10/25 轮播改版）：把这条样片的 prompt 和原图填进模板左栏。
	// /ai-edit-image 的 Examples 轮播（10/27）也发这个事件：切到图生图，原图照 handleUseTemplate 一律转 base64。
	// setPrompt 跟着 activeTab 换引用，放进依赖的话从文生图点过来，切 tab 会让 effect 重订阅，
	// 还在转 base64 的这次被当过期丢掉，prompt 和原图都填不进去（10/28 Codex review 发现，实测复现）。这里都显式传 tab，用 ref 拿最新的
	const setPromptRef = useRef(setPrompt);
	setPromptRef.current = setPrompt;
	useEffect(() => {
		let requestId = 0;
		const onSetExample = async (event: Event) => {
			const example = (event as CustomEvent<ImageHeroExample>).detail;
			if (!example) return;
			const currentRequest = ++requestId;
			if (!isOnTemplateRoute) {
				setActiveTab("image-to-image");
				setIsUploading(true);
				try {
					const images = await Promise.all(
						example.images.map((url) => convertImageUrlToBase64(url)),
					);
					if (currentRequest !== requestId) return;
					setInputImages(images);
					setUploadedImagePreviews(images);
					setPromptRef.current(example.prompt, "image-to-image");
				} catch {
					if (currentRequest === requestId)
						toast.error("Failed to load template. Please try again.");
				} finally {
					if (currentRequest === requestId) setIsUploading(false);
				}
				return;
			}
			const isImageEffect =
				templateIdFromUrl === "ai-smile-generator" ||
				templateIdFromUrl === "image-to-emoji-converter" ||
				templateIdFromUrl === "ai-muscle-generator" ||
				templateIdFromUrl === "ai-big-butt" ||
				templateIdFromUrl === "ai-feet-generator";
			if (isImageEffect) setIsUploading(true);
			try {
				// Local example paths cannot be fetched by the upstream model.
				const images = isImageEffect
					? await Promise.all(
							example.images.map((url) =>
								url.startsWith("/")
									? convertImageUrlToBase64(url)
									: url,
							),
						)
					: example.images;
				if (currentRequest !== requestId) return;
				setInputImages(images);
				setUploadedImagePreviews(images);
				setPromptRef.current(example.prompt, "templates");
				if (example.aspectRatio) setAspectRatio(example.aspectRatio);
				if (example.resolution) {
					setResolution(example.resolution);
					setImageCount(1);
				}
			} catch {
				if (currentRequest === requestId)
					toast.error("Failed to load template. Please try again.");
			} finally {
				if (isImageEffect && currentRequest === requestId)
					setIsUploading(false);
			}
		};
		window.addEventListener(IMAGE_HERO_EXAMPLE_EVENT, onSetExample);
		return () => {
			requestId++;
			window.removeEventListener(IMAGE_HERO_EXAMPLE_EVENT, onSetExample);
		};
	}, [
		isOnTemplateRoute,
		templateIdFromUrl,
		convertImageUrlToBase64,
		setInputImages,
		setAspectRatio,
		setResolution,
		setImageCount,
	]);

	const handleReprompt = async (item: ImageGeneration) => {
		try {
			// 别的页点姿势页出的图：带着记录回姿势页去填（image-home.ts）
			if (routeImageHome(item, "reprompt")) {
				return;
			}

			// Determine the input type - check inputType field or infer from inputImageUrls
			const isImageToImage =
				item.inputType === "image" ||
				(item.inputImageUrls && item.inputImageUrls.length > 0);

			const targetTab = isImageToImage
				? "image-to-image"
				: "text-to-image";

			// 姿势页试点「作品回出生页」：本页出的图原地填回模板左栏；
			// 别的页出的、10/23 前没记 page 的照旧去 edit 页
			const refillInTemplate =
				isOnTemplateRoute &&
				!!templateIdFromUrl &&
				getImageHomePage(item) === getTemplatePath(templateIdFromUrl);

			// If on template route, navigate to main generator page with sessionStorage
			if (isOnTemplateRoute && !refillInTemplate) {
				// Store reprompt data in sessionStorage
				const repromptData = {
					prompt: item.originalPrompt,
					inputType: isImageToImage ? "image" : "text",
					inputImageUrls: item.inputImageUrls || [],
				};
				sessionStorage.setItem(
					"repromptData",
					JSON.stringify(repromptData),
				);

				// Navigate to main generator page with action and tab parameters
				// Build path with locale prefix if needed (as-needed strategy)
				const path =
					locale === config.i18n.defaultLocale
						? `/ai-edit-image?action=reprompt&tab=${encodeURIComponent(targetTab)}`
						: `/${locale}/ai-edit-image?action=reprompt&tab=${encodeURIComponent(targetTab)}`;
				router.push(path);
				return;
			}

			// If not on template route, handle reprompt in current page
			if (refillInTemplate) {
				// 没传图的记录也要清掉上一张图：可选传图的模板（脚图页）不清会变成照片模式
				const urls = item.inputImageUrls ?? [];
				setInputImages(urls);
				setUploadedImagePreviews(urls);
				setPrompt(item.originalPrompt, "templates");
			} else if (isImageToImage) {
				// Switch to image-to-image tab
				setActiveTab("image-to-image");

				// Set input images if available (use URLs directly, backend supports them)
				if (item.inputImageUrls && item.inputImageUrls.length > 0) {
					// Backend supports URL format, so we can use URLs directly
					setInputImages(item.inputImageUrls);
					setUploadedImagePreviews(item.inputImageUrls);
				}

				// Set prompt
				setPrompt(item.originalPrompt, "image-to-image");
			} else {
				// Switch to text-to-image tab
				setActiveTab("text-to-image");

				// Only set prompt for text-to-image
				setPrompt(item.originalPrompt, "text-to-image");
			}

			// Scroll to left panel on small screens where layout is vertical
			setTimeout(() => {
				if (window.innerWidth < 768) {
					const leftPanel = document.getElementById(
						"ai-image-generator-left-panel",
					);
					if (leftPanel) {
						leftPanel.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
					}
				}
			}, 100);
		} catch (error) {
			console.error("Failed to reprompt:", error);
			toast.error(t("imageSettings.errors.loadRepromptFailed"));
		}
	};

	const handleRegenerate = async (historyItem: ImageGeneration) => {
		// Don't update left panel state, just regenerate with saved config
		if (generateImageMutation.isPending) return;

		// 别的页点姿势页出的图：回姿势页去重跑，出的新图也记在姿势页名下
		if (routeImageHome(historyItem, "regenerate")) {
			return;
		}

		// Check if user is authenticated
		if (!user) {
			const returnUrl = pathname;
			router.push(
				`/auth/login?redirectTo=${encodeURIComponent(returnUrl)}`,
			);
			return;
		}

		// Extract configuration from history item metadata
		const params = historyItem.metadata?.parameters || {};
		// 历史记录里可能存着已下架的模型 id，请求 schema 现在是严格 enum，不映射会 400
		const model = coerceRetiredModel(
			(params.model || historyItem.model) as string,
		);

		const aspectRatio = params.aspect_ratio || "1:1";
		const numOutputs = params.num_outputs || 1;

		// Extract resolution from metadata
		let resolution: "1K" | "2K" | "4K" = "1K";

		if (params.resolution) {
			resolution = params.resolution as "1K" | "2K" | "4K";
		} else if (historyItem.metadata?.provider?.parameters?.resolution) {
			// Some providers save resolution in their parameters
			resolution = historyItem.metadata.provider.parameters.resolution as
				| "1K"
				| "2K"
				| "4K";
		}

		// 免费用户重新生成旧的 2K/4K 记录时，后端会静默降到 1K。下面的费用预检和
		// 乐观记录都得用降级后的值，否则会按高清价误弹 paywall。
		resolution = effectiveResolution(model, resolution);

		// Determine input type
		const inputType: "text" | "image" =
			historyItem.inputType === "image" ||
			(historyItem.inputImageUrls &&
				historyItem.inputImageUrls.length > 0)
				? "image"
				: "text";

		const creditCost = getImageGenerationCreditCost(
			model,
			resolution,
			numOutputs,
			modelConfigs,
			// 跟下面带不带 image_input 同一个判据
			inputType === "image" &&
				(historyItem.inputImageUrls?.length ?? 0) > 0,
		);

		// Check credits
		const remainingCredits =
			await getRemainingCreditsWhenLoaded("IMAGE_GENERATION");
		// 上一次点击还在等积分，忽略这次，免得生成两次
		if (remainingCredits === null) return;
		if (remainingCredits < creditCost) {
			setUpgradeModalReason("insufficient-credits");
			setShowUpgradeModal(true);
			return;
		}

		// Set regenerating state
		setRegeneratingItemId(historyItem.id);

		// Prepare request data
		const requestData: {
			prompt: string;
			model: ImageGeneratorApiModel;
			aspect_ratio?: string;
			num_outputs?: number;
			isPublic?: boolean;
			resolution?: string;
			inputType?: "text" | "image";
			input_image?: string;
			image_input?: string[];
		} = {
			prompt: historyItem.originalPrompt,
			model,
			aspect_ratio: aspectRatio,
			num_outputs: numOutputs,
			isPublic: false,
			inputType,
		};

		// Add input images for image-to-image mode
		if (
			inputType === "image" &&
			historyItem.inputImageUrls &&
			historyItem.inputImageUrls.length > 0
		) {
			requestData.image_input = historyItem.inputImageUrls;
		}

		// Add resolution for models that support it
		if (supportsImageResolution(model)) {
			requestData.resolution = resolution;
		}

		// Create optimistic pending item
		addPendingItem({
			id: `pending-${Date.now()}`,
			originalPrompt: historyItem.originalPrompt,
			model: model,
			imageUrls: [],
			inputImageUrls: historyItem.inputImageUrls,
			inputType: inputType,
			createdAt: new Date().toISOString(),
			status: "PENDING",
			metadata: {
				timestamp: new Date().toISOString(),
				parameters: {
					model,
					aspect_ratio: aspectRatio,
					num_outputs: numOutputs,
					resolution,
				},
			},
		});

		// Scroll to top immediately to show the optimistic pending item
		setTimeout(() => {
			if (historyScrollRef.current) {
				// On small screens, first scroll the page to the history container element
				if (window.innerWidth < 768) {
					historyScrollRef.current.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
					// Then scroll the history container content to top
					setTimeout(() => {
						if (historyScrollRef.current) {
							historyScrollRef.current.scrollTo({
								top: 0,
								behavior: "smooth",
							});
						}
					}, 300);
				} else {
					// On large screens, just scroll the history container content
					historyScrollRef.current.scrollTo({
						top: 0,
						behavior: "smooth",
					});
				}
			}
		}, 50);

		// Call mutation with custom callbacks to clear regenerating state
		generateImageMutation.mutate(requestData, {
			onSuccess: () => {
				setRegeneratingItemId(null);

				// Scroll to top again after regeneration completes and data refreshes
				// Use setTimeout to ensure DOM has updated after invalidateQueries
				setTimeout(() => {
					if (historyScrollRef.current) {
						// On small screens, first scroll the page to the history container element
						if (window.innerWidth < 768) {
							historyScrollRef.current.scrollIntoView({
								behavior: "smooth",
								block: "start",
							});
							// Then scroll the history container content to top
							setTimeout(() => {
								if (historyScrollRef.current) {
									historyScrollRef.current.scrollTo({
										top: 0,
										behavior: "smooth",
									});
								}
							}, 300);
						} else {
							// On large screens, just scroll the history container content
							historyScrollRef.current.scrollTo({
								top: 0,
								behavior: "smooth",
							});
						}
					}
				}, 300);
			},
			onError: () => {
				setRegeneratingItemId(null);
				// 占位卡由 mutation 自己的 onError 撤掉
			},
		});
	};

	// Handle file selection for templates (upload to R2 via presigned URL)
	const handleTemplateFileSelect = async (files: FileList | null) => {
		if (!files || files.length === 0) {
			return;
		}

		const fileArray = Array.from(files);
		const maxFiles = selectedTemplate?.behavior?.maxImages ?? 2; // Default to 2, but allow template override
		const currentCount = inputImages.length;
		const remainingSlots = maxFiles - currentCount;
		// Single-image templates swap the existing image instead of rejecting the upload
		const effectiveReplaceIndex =
			maxFiles === 1 && currentCount >= maxFiles ? 0 : null;

		if (effectiveReplaceIndex !== null) {
			setIsUploading(true);
			try {
				const compressedFile = await compressImage(fileArray[0], {
					maxWidthOrHeight: 1024,
					initialQuality: 0.8,
					fileType: "image/webp",
				});
				const { uploadImageToR2Directly } = await import(
					"@shared/lib/r2-direct-upload"
				);
				const imageUrl = await uploadImageToR2Directly(compressedFile);
				const nextImages = inputImages.map((image, index) =>
					index === effectiveReplaceIndex ? imageUrl : image,
				);
				setInputImages(nextImages);
				setUploadedImagePreviews(nextImages);
			} catch (error) {
				console.error("Failed to process image:", error);
				toast.error("Failed to process image");
			} finally {
				setIsUploading(false);
			}
			return;
		}

		if (remainingSlots <= 0) {
			toast.error(`Maximum ${maxFiles} images allowed`);
			return;
		}

		if (fileArray.length > remainingSlots) {
			toast.error(
				`You can only add ${remainingSlots} more image${remainingSlots > 1 ? "s" : ""}`,
			);
			return;
		}

		setIsUploading(true);
		try {
			const uploadPromises = fileArray
				.slice(0, remainingSlots)
				.map(async (file) => {
					const compressedFile = await compressImage(file, {
						maxWidthOrHeight: 1024,
						initialQuality: 0.8,
						fileType: "image/webp",
					});
					const { uploadImageToR2Directly } = await import(
						"@shared/lib/r2-direct-upload"
					);
					return uploadImageToR2Directly(compressedFile);
				});

			const imageUrls = await Promise.all(uploadPromises);
			const nextImages = [...inputImages, ...imageUrls];
			setInputImages(nextImages);
			setUploadedImagePreviews(nextImages);
		} catch (error) {
			console.error("Failed to process images:", error);
			toast.error("Failed to process images");
		} finally {
			setIsUploading(false);
		}
	};

	// Handle file selection
	const handleFileSelect = async (files: FileList | File[] | null) => {
		if (!files || files.length === 0) return;

		const fileArray = Array.from(files);
		const maxFiles = maxInputImages;
		const currentCount = uploadedImagePreviews.length;
		const remainingSlots = maxFiles - currentCount;

		if (remainingSlots <= 0) {
			toast.error(
				t("imageSettings.upload.maxImagesError", {
					max: maxFiles,
				}),
			);
			return;
		}

		if (fileArray.length > remainingSlots) {
			toast.error(
				t("imageSettings.upload.limitReached", {
					remaining: remainingSlots,
					plural: remainingSlots > 1 ? "s" : "",
				}),
			);
			return;
		}

		setIsUploading(true);
		try {
			const uploadPromises = fileArray.map(async (file) => {
				// Compress image: 2MB max, 1280px resolution, WebP format
				const compressedFile = await compressImage(file, {
					maxSizeMB: 2,
					maxWidthOrHeight: 1280,
					fileType: "image/webp",
				});
				// Upload directly to R2
				const { uploadImageToR2Directly } = await import(
					"@shared/lib/r2-direct-upload"
				);
				return uploadImageToR2Directly(compressedFile);
			});

			const imageUrls = await Promise.all(uploadPromises);
			setInputImages([...inputImages, ...imageUrls]);
			setUploadedImagePreviews([...uploadedImagePreviews, ...imageUrls]);
			trackAiEditImageEvent(pathname, "ai-edit-image:image-uploaded", {
				count: imageUrls.length,
			});
		} catch (error) {
			console.error("Failed to process images:", error);
			toast.error(t("imageSettings.errors.processImagesFailed"));
		} finally {
			setIsUploading(false);
		}
	};

	// Handle drag and drop
	const handleDragOver = (e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(true);
	};

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
	};

	const handleDrop = (e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);

		const files = e.dataTransfer.files;
		if (files && files.length > 0) {
			handleFileSelect(files);
		}
	};

	// Pasting an image anywhere on the page uploads it, even while typing the
	// prompt; text pastes go through untouched. The asset picker handles paste
	// itself while it is open, so this one steps aside.
	useEffect(() => {
		if (activeTab !== "image-to-image" || isAssetDialogOpen) {
			return;
		}

		const handlePaste = (event: ClipboardEvent) => {
			const files = Array.from(event.clipboardData?.files ?? []).filter(
				(file) => file.type.startsWith("image/"),
			);
			if (files.length === 0 || isUploading) {
				return;
			}
			event.preventDefault();
			handleFileSelect(files);
		};
		window.addEventListener("paste", handlePaste);

		return () => window.removeEventListener("paste", handlePaste);
	}, [activeTab, isAssetDialogOpen, isUploading, handleFileSelect]);

	// Handle drop for templates
	const handleTemplateDrop = (e: DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);

		const files = e.dataTransfer.files;
		if (files && files.length > 0) {
			handleTemplateFileSelect(files);
		}
	};

	// Handle click to upload
	const handleClickUpload = () => {
		fileInputRef.current?.click();
	};

	// Handle file input change
	const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		handleFileSelect(e.target.files);
		// Reset input so same file can be selected again
		if (e.target) {
			e.target.value = "";
		}
	};

	// 删图 / Clear all 改了 prompt 里的编号时给一个 Undo：文字是「自己变」的，用户得看得见、退得回。
	// 只在删完之后什么都没动时有效：`after` 是删完那一刻的图和文字，之后打字、加删图、切 tab
	// 一对不上就把 Toast 关掉，否则 Undo 会把后来打的字和加的图一起退掉（10/22 Codex）
	const promptUndoRef = useRef<{
		toastId: string | number;
		images: string[];
		previews: string[];
		prompt: string;
	} | null>(null);
	const offerPromptUndo = (
		message: string,
		after: { images: string[]; previews: string[]; prompt: string },
	) => {
		const previous = {
			images: inputImages,
			previews: uploadedImagePreviews,
			prompt,
		};
		// 同一时间只留一个有效的 Undo：连删两张时第一个已经对不上了
		if (promptUndoRef.current) {
			toast.dismiss(promptUndoRef.current.toastId);
		}
		const toastId = toast(message, {
			action: {
				label: t("imageRefs.undo"),
				onClick: () => {
					promptUndoRef.current = null;
					setInputImages(previous.images);
					setUploadedImagePreviews(previous.previews);
					setPrompt(previous.prompt);
				},
			},
		});
		promptUndoRef.current = { toastId, ...after };
	};

	useEffect(() => {
		const pending = promptUndoRef.current;
		const differs = (a: string[], b: string[]) =>
			a.length !== b.length || a.some((url, i) => url !== b[i]);
		if (
			pending &&
			(pending.prompt !== prompt ||
				differs(pending.images, inputImages) ||
				differs(pending.previews, uploadedImagePreviews))
		) {
			toast.dismiss(pending.toastId);
			promptUndoRef.current = null;
		}
	}, [prompt, inputImages, uploadedImagePreviews]);

	// Remove image
	const handleRemoveImage = (index: number) => {
		const newImages = inputImages.filter((_, i) => i !== index);
		const newPreviews = uploadedImagePreviews.filter((_, i) => i !== index);
		// 指向这张的「Image N」删掉、后面的编号往前补，否则会悄悄改指下一张
		const nextPrompt =
			activeTab === "image-to-image"
				? removeImageRef(prompt, index + 1)
				: prompt;
		if (nextPrompt !== prompt) {
			offerPromptUndo(
				t("imageRefs.removedFromPrompt", { n: index + 1 }),
				{
					images: newImages,
					previews: newPreviews,
					prompt: nextPrompt,
				},
			);
			setPrompt(nextPrompt);
		}
		setInputImages(newImages);
		setUploadedImagePreviews(newPreviews);
	};

	// Replace an uploaded image with its cropped version. The crop dialog hands
	// back a blob, so it still has to go through compression + R2 like an upload.
	const handleEditConfirm = async (blob: Blob) => {
		const index = editImageIndex;
		if (index === null) {
			return;
		}

		const croppedFile = new File([blob], "cropped.webp", {
			type: blob.type || "image/webp",
		});
		const compressedFile = await compressImage(croppedFile, {
			maxSizeMB: 2,
			maxWidthOrHeight: 1280,
			fileType: "image/webp",
		});
		const { uploadImageToR2Directly } = await import(
			"@shared/lib/r2-direct-upload"
		);
		const imageUrl = await uploadImageToR2Directly(compressedFile);

		setInputImages(
			inputImages.map((image, i) => (i === index ? imageUrl : image)),
		);
		setUploadedImagePreviews(
			uploadedImagePreviews.map((image, i) =>
				i === index ? imageUrl : image,
			),
		);
	};

	// The picker reads the signed-in user's assets, so anonymous visitors get the
	// login modal instead of an empty dialog that 401s.
	const openAssetDialog = () => {
		if (!user) {
			window.dispatchEvent(
				new CustomEvent("show-login", {
					detail: {
						redirectTo:
							window.location.pathname + window.location.search,
					},
				}),
			);
			return;
		}
		setIsAssetDialogOpen(true);
	};

	// Reuse images the user already has in My Assets — they're already on R2, so
	// there is nothing to upload, just URLs to add.
	const handleSelectAssets = (urls: string[]) => {
		const newUrls = urls.filter(
			(url) => !uploadedImagePreviews.includes(url),
		);
		const insertRefs = browseInsertsRefsRef.current;
		browseInsertsRefsRef.current = false;
		// 已有图复用编号，新图排在最后；引用按用户选图的顺序插入。
		if (insertRefs) {
			const nextPreviews = [...uploadedImagePreviews, ...newUrls];
			promptHandleRef.current?.insertImageRefs(
				urls.map((url) => nextPreviews.indexOf(url) + 1),
			);
			trackAiEditImageEvent(
				pathname,
				"ai-edit-image:reference-inserted",
				{
					from: "browse",
					count: urls.length,
				},
			);
		}
		if (newUrls.length === 0) {
			return;
		}
		setInputImages([...inputImages, ...newUrls]);
		setUploadedImagePreviews([...uploadedImagePreviews, ...newUrls]);
	};

	// Clear all images
	const handleClearAllImages = () => {
		const nextPrompt = dropAllImageRefs(prompt);
		if (nextPrompt !== prompt) {
			offerPromptUndo(t("imageRefs.clearedFromPrompt"), {
				images: [],
				previews: [],
				prompt: nextPrompt,
			});
			setPrompt(nextPrompt);
		}
		setInputImages([]);
		setUploadedImagePreviews([]);
	};

	// @ Reference：按钮或在 prompt 里敲 @ 打开
	const openRefPicker = (via: "button" | "typed") => {
		if (via === "button") {
			promptHandleRef.current?.cancelImageRef();
		}
		setIsRefPickerOpen(true);
		trackAiEditImageEvent(pathname, "ai-edit-image:reference-opened", {
			via,
		});
	};

	const closeRefPicker = useCallback(() => {
		promptHandleRef.current?.cancelImageRef();
		setIsRefPickerOpen(false);
	}, []);

	const insertExistingImageRef = (n: number) => {
		promptHandleRef.current?.insertImageRefs([n]);
		setIsRefPickerOpen(false);
		trackAiEditImageEvent(pathname, "ai-edit-image:reference-inserted", {
			from: "in-use",
		});
	};

	// 素材库里的图：先加进上传区当最后一张，再插它的编号
	const insertAssetImageRef = (url: string, from: "recent" | "favorite") => {
		if (uploadedImagePreviews.length >= maxInputImages) {
			return;
		}
		promptHandleRef.current?.insertImageRefs([
			uploadedImagePreviews.length + 1,
		]);
		setInputImages([...inputImages, url]);
		setUploadedImagePreviews([...uploadedImagePreviews, url]);
		setIsRefPickerOpen(false);
		trackAiEditImageEvent(pathname, "ai-edit-image:reference-inserted", {
			from,
		});
	};

	const browseAllForRef = () => {
		setIsRefPickerOpen(false);
		browseInsertsRefsRef.current = true;
		openAssetDialog();
	};

	// Create 按钮照 DeeVid 一直是品牌色、不置灰（10/19 对比页第 9 项），缺东西点了再提示。
	// 条件和原来的 disabled 一致；先查图再查字，和界面从上到下的顺序一样
	const missingInput: "image" | "prompt" | null = (() => {
		const hidePrompt =
			activeTab === "templates" && selectedTemplate?.behavior?.hidePrompt;
		const needsImage =
			activeTab === "image-to-image" ||
			(activeTab === "templates" &&
				(hidePrompt ||
					(!!selectedTemplate?.behavior?.maxImages &&
						!selectedTemplate.behavior.optionalImage)));
		if (needsImage && inputImages.length === 0) {
			return "image";
		}
		if (!hidePrompt && !prompt.trim()) {
			return "prompt";
		}
		return null;
	})();

	// @ Reference 只在图生图里有；没登录又没传图时什么都不弹（和 DeeVid 一样，10/21 老公定不放登录框）
	const canReferenceImages =
		activeTab === "image-to-image" &&
		(!!user || uploadedImagePreviews.length > 0);

	// prompt 里写了不存在的编号（image 4 但只有 2 张）：只提示，不拦 Create
	const missingImageRef =
		activeTab === "image-to-image" && uploadedImagePreviews.length > 0
			? firstMissingImageRef(prompt, uploadedImagePreviews.length)
			: null;

	// Generate image with scroll to history on large screens
	const handleGenerateWithScroll = async () => {
		// 缺图/缺字不再弹提示（10/21 老公定）：登录用户点了就让缺的那一栏闪红框；
		// 访客点了弹登录框
		if (missingInput) {
			if (!user) {
				void requestLogin();
			} else {
				flashMissingField(
					missingInput === "image"
						? document.querySelector('[data-missing-field="image"]')
						: document.getElementById("prompt"),
				);
			}
			return;
		}

		// Pre-check credits before making any API call
		if (user) {
			const remainingCredits =
				await getRemainingCreditsWhenLoaded("IMAGE_GENERATION");
			// 上一次点击还在等积分，忽略这次，免得生成两次
			if (remainingCredits === null) return;
			if (remainingCredits < currentCreditCost) {
				setUpgradeModalReason("insufficient-credits");
				setShowUpgradeModal(true);
				return;
			}
		}

		// Call the original generate function (don't await - we want to scroll immediately after optimistic update)
		handleGenerateImage();

		// Scroll to top of history panel to show the generating item
		// Use setTimeout to ensure the optimistic item has been added to the DOM
		setTimeout(() => {
			if (historyScrollRef.current) {
				// On small screens, first scroll the page to the history container element
				if (window.innerWidth < 768) {
					historyScrollRef.current.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});
					// Then scroll the history container content to top
					setTimeout(() => {
						if (historyScrollRef.current) {
							historyScrollRef.current.scrollTo({
								top: 0,
								behavior: "smooth",
							});
						}
					}, 300);
				} else {
					// On large screens, just scroll the history container content to top
					historyScrollRef.current.scrollTo({
						top: 0,
						behavior: "smooth",
					});
				}
			}
		}, 150);
	};

	// Download image
	const handleDownloadImage = async (
		imageUrl: string,
		item: ImageGeneration,
		index?: number,
	) => {
		if (downloadingImages.has(imageUrl)) return;

		setDownloadingImages((prev) => new Set(prev).add(imageUrl));
		const toastId = toast.loading(
			`${t("imageSettings.actions.download")}...`,
		);

		try {
			// Use proxy endpoint to avoid CORS issues
			const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(imageUrl)}`;
			const response = await fetch(proxyUrl);

			if (!response.ok) {
				let errorMessage = `Failed to fetch image: ${response.status} ${response.statusText}`;
				try {
					const errorText = await response.text();
					const errorJson = JSON.parse(errorText);
					if (errorJson.error) {
						errorMessage = errorJson.error;
					}
				} catch {
					// If parsing fails, use the default error message
				}

				// Log detailed error for debugging
				console.error("Image proxy failed:", {
					status: response.status,
					statusText: response.statusText,
					imageUrl,
					errorMessage,
				});

				throw new Error(errorMessage);
			}

			let blob = await response.blob();

			// Apply watermark unless the user can have watermark-free output
			if (!canRemoveWatermark) {
				try {
					// Import watermark function
					const { addCanvasWatermark } = await import(
						"@shared/lib/watermark-canvas"
					);

					// Apply watermark to the blob
					blob = await addCanvasWatermark(blob, {
						text: "ImagesArt.ai",
						position: "top-right",
						fontSize: 26,
						color: "rgba(255, 255, 255, 0.4)",
						margin: 30,
						opacity: 0.6,
					});

					console.log(
						"[Download] Watermark added to downloaded image",
					);
				} catch (watermarkError) {
					console.error(
						"[Download] Failed to add watermark, downloading original:",
						watermarkError,
					);
					// Continue with original blob if watermark fails
				}
			}

			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;

			// The downloaded blob may differ from the requested format (or be watermarked).
			const mimeType = blob.type.split(";")[0].toLowerCase();
			const mimeExtensions: Record<string, string> = {
				"image/jpeg": "jpg",
				"image/png": "png",
				"image/webp": "webp",
				"image/gif": "gif",
				"image/avif": "avif",
			};
			const requestedFormat = item.metadata?.parameters?.output_format;
			const fallbackFormat =
				(typeof requestedFormat === "string" &&
					/^(jpg|jpeg|png|webp)$/.test(requestedFormat.toLowerCase()) &&
					requestedFormat.toLowerCase()) ||
				imageUrl.match(/\.(jpg|jpeg|png|webp)(?:[?#]|$)/i)?.[1]?.toLowerCase() ||
				"png";
			const fileExtension =
				mimeExtensions[mimeType] || (fallbackFormat === "jpeg" ? "jpg" : fallbackFormat);

			// Generate filename with format: {userid}-YYYYMMDD-HHMMSS[-index].{ext}
			// Get userId with fallback for unauthenticated users
			const userId = user?.id || "guest";

			// Format timestamp as YYYYMMDD-HHMMSS
			let timestampStr: string;
			try {
				// Priority 1: Use item.createdAt if available
				let date: Date;
				if (item.createdAt) {
					date = new Date(item.createdAt);
				} else if (item.metadata?.timestamp) {
					// Priority 2: Use metadata.timestamp
					date = new Date(item.metadata.timestamp);
				} else {
					// Priority 3: Use current time
					date = new Date();
				}

				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, "0");
				const day = String(date.getDate()).padStart(2, "0");
				const hours = String(date.getHours()).padStart(2, "0");
				const minutes = String(date.getMinutes()).padStart(2, "0");
				const seconds = String(date.getSeconds()).padStart(2, "0");
				timestampStr = `${year}${month}${day}-${hours}${minutes}${seconds}`;
			} catch {
				// Fallback to current timestamp if parsing fails
				const now = new Date();
				const year = now.getFullYear();
				const month = String(now.getMonth() + 1).padStart(2, "0");
				const day = String(now.getDate()).padStart(2, "0");
				const hours = String(now.getHours()).padStart(2, "0");
				const minutes = String(now.getMinutes()).padStart(2, "0");
				const seconds = String(now.getSeconds()).padStart(2, "0");
				timestampStr = `${year}${month}${day}-${hours}${minutes}${seconds}`;
			}

			// Add index suffix for multiple images
			let fileName: string;
			if (index !== undefined && item.imageUrls.length > 1) {
				// Multiple images: add index suffix (1-based)
				fileName = `${userId}-${timestampStr}-${index + 1}.${fileExtension}`;
			} else {
				// Single image: no index suffix
				fileName = `${userId}-${timestampStr}.${fileExtension}`;
			}

			a.download = fileName;
			document.body.appendChild(a);
			a.click();

			toast.success(`${t("imageSettings.actions.download")} Success`, {
				id: toastId,
			});

			// Delay revocation to ensure browser has started the download
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
				if (document.body.contains(a)) {
					document.body.removeChild(a);
				}
			}, 60000); // 60 seconds is safe for even huge files
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";
			console.error("Failed to download image:", {
				error: errorMessage,
				imageUrl,
				generationId: item.id,
			});
			toast.error(t("imageSettings.errors.downloadFailed"), {
				id: toastId,
			});
		} finally {
			setDownloadingImages((prev) => {
				const next = new Set(prev);
				next.delete(imageUrl);
				return next;
			});
		}
	};

	// Favorite toggle - applies to a single image, same as My Assets
	const handleFavoriteToggle = async (
		id: string,
		imageIndex: number,
		currentFavorite: boolean,
	) => {
		const newFavoriteState = !currentFavorite;
		const key = `${id}:${imageIndex}`;

		setFavoriteOverrides((prev) => ({ ...prev, [key]: newFavoriteState }));

		try {
			const response = await (
				apiClient["image-generation"][":id"]["favorite"].$patch as any
			)({
				param: { id },
				json: { favorite: newFavoriteState, imageIndex },
			});

			if (!response.ok) {
				throw new Error("Failed to update favorite");
			}

			queryClient.invalidateQueries({
				queryKey: GENERATION_HISTORY_QUERY_KEY,
			});

			toast.success(
				newFavoriteState
					? tAssets("notifications.favoriteAdded")
					: tAssets("notifications.favoriteRemoved"),
			);
		} catch (error) {
			setFavoriteOverrides((prev) => ({
				...prev,
				[key]: currentFavorite,
			}));
			console.error("Error toggling favorite:", error);
			toast.error(tAssets("errors.favoriteFailed"));
		}
	};

	// 视频删的是整条记录（一条记录就一个成片），不像图片要带下标
	const handleDeleteVideo = (item: VideoHistoryItem) => {
		confirm({
			title: tAssets("deleteDialog.title"),
			message: tAssets("deleteDialog.descriptionVideo"),
			confirmLabel: tAssets("deleteDialog.confirm"),
			destructive: true,
			onConfirm: async () => {
				try {
					const response = await apiClient["ai-video"][
						":taskId"
					].$delete({ param: { taskId: item.id } });
					if (!response.ok) {
						throw new Error("Failed to delete");
					}
					queryClient.invalidateQueries({
						queryKey: GENERATION_HISTORY_QUERY_KEY,
					});
					toast.success(tAssets("notifications.deleteVideoSuccess"));
					setSelectedVideo(null);
				} catch (error) {
					console.error("Error deleting video:", error);
					toast.error(tAssets("errors.deleteFailed"));
				}
			},
		});
	};

	// Delete a single image out of a generation's imageUrls.
	// onDeleted only fires on success — the detail modal closes itself with it.
	const handleDeleteImage = (
		id: string,
		imageIndex: number,
		onDeleted?: () => void,
	) => {
		confirm({
			title: tAssets("deleteDialog.title"),
			message: tAssets("deleteDialog.description"),
			confirmLabel: tAssets("deleteDialog.confirm"),
			destructive: true,
			onConfirm: async () => {
				try {
					const response = await fetch(
						`/api/image-generation/${id}?imageIndex=${imageIndex}`,
						{ method: "DELETE", credentials: "include" },
					);

					if (!response.ok) {
						const errorData = await response.json();
						throw new Error(
							(errorData as { error?: string }).error ||
								"Failed to delete",
						);
					}

					// The server shifts every favorite index after the deleted
					// one, so overrides keyed by the old index now point at the
					// wrong image — drop this generation's entries.
					setFavoriteOverrides((prev) =>
						Object.fromEntries(
							Object.entries(prev).filter(
								([key]) => !key.startsWith(`${id}:`),
							),
						),
					);

					queryClient.invalidateQueries({
						queryKey: GENERATION_HISTORY_QUERY_KEY,
					});

					toast.success(tAssets("notifications.deleteSuccess"));

					onDeleted?.();
				} catch (error) {
					console.error("Error deleting image:", error);
					toast.error(
						error instanceof Error
							? error.message
							: tAssets("errors.deleteFailed"),
					);
				}
			},
		});
	};

	// Edit image - set image to image-to-image upload box
	const handleEditImage = (imageUrl: string) => {
		try {
			// If on template route, navigate to /ai-edit-image with the image
			if (isOnTemplateRoute) {
				// Store image URL in sessionStorage for retrieval after navigation
				sessionStorage.setItem(
					"editImageData",
					JSON.stringify({ imageUrl }),
				);
				// Build path with locale prefix if needed (as-needed strategy)
				const path =
					locale === config.i18n.defaultLocale
						? "/ai-edit-image?action=edit"
						: `/${locale}/ai-edit-image?action=edit`;
				router.push(path);
				return;
			}

			// Switch to image-to-image tab
			setActiveTab("image-to-image");

			// Set default model when editing
			setSelectedModel(pageDefaultModel);

			// Clear prompt text area for fresh input
			setPrompt("", "image-to-image");

			// Clear existing images
			setInputImages([]);
			setUploadedImagePreviews([]);

			// Set the selected image URL (backend supports URL format)
			setInputImages([imageUrl]);
			setUploadedImagePreviews([imageUrl]);

			// Scroll to left panel on small screens where layout is vertical
			setTimeout(() => {
				if (window.innerWidth < 768) {
					const leftPanel = document.getElementById(
						"ai-image-generator-left-panel",
					);
					if (leftPanel) {
						leftPanel.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
					}
				}
			}, 100);
		} catch (error) {
			console.error("Failed to set image:", error);
			toast.error("Failed to set image. Please try again.");
		}
	};

	// Video items in the shared history feed render their status with the same
	// wording as image items
	const historyStatusLabels = useMemo(
		() => ({
			generating: t("imageSettings.status.generating"),
			failed: t("imageSettings.status.failed"),
		}),
		[t],
	);

	// 下拉里隐藏的模型（如待恢复状态里的 nano-banana-2）去全量清单查，别兜底
	// 成 modelList[0]——那样显示 V3.0、请求和扣费却是别的模型
	const selectedModelDetails =
		modelList.find((m) => m.id === selectedModel) ||
		allModels.find((m) => m.id === selectedModel) ||
		modelList[0];
	const visibleAspectRatios = useMemo(
		() =>
			aspectRatios.filter((ratio) => {
				// auto 只对上游真正支持它的模型开放
				if (ratio.value === "auto") {
					return supportsAutoAspectRatio(selectedModel);
				}
				return true;
			}),
		[aspectRatios, selectedModel],
	);
	// 模板带默认比例、当前还是 original 时显示模板的默认比例（原来模板页的比例下拉就这么显示）
	const selectedAspectRatioLabel =
		(size === "original" &&
			validTemplateFromUrl?.behavior?.defaultAspectRatio) ||
		aspectRatios.find((ratio) => ratio.value === size)?.label ||
		"1:1";
	const selectedResolutionLabel = getResolutionLabel(
		selectedModel,
		resolution,
	).label;
	// 配置条「1:1 | 2K」，和视频页同一规则：张数是 1 不显示（99% 的生成只出 1 张，单写个「1」看不懂），多出片才带「2x」（10/24）
	const compactSettingsParts = [
		...(validTemplateFromUrl?.behavior?.hideAspectRatio
			? []
			: [{ key: "ratio", label: selectedAspectRatioLabel }]),
		...(showResolutionSelector
			? [{ key: "resolution", label: selectedResolutionLabel }]
			: []),
		...(imageCount > 1 ? [{ key: "count", label: `${imageCount}x` }] : []),
	];

	// 历史卡片 memo 化（HistoryFeedRows）：handlers 经 ref 转发，引用永不变但总调到
	// 最新实现；label 对象和 modelLabel 也 memo 住。这样提示词敲字时列表一张卡都不重渲染
	const rowHandlersRef = useRef({
		handleReprompt,
		handleRegenerate,
		handleGenerateVideo,
		handleDownloadImage,
		handleEditImage,
		handleDeleteImage,
		handleFavoriteToggle,
		setSelectedImage,
		handleVideoAction,
		setSelectedVideo,
		toggleVideoFavorite,
		handleDeleteVideo,
	});
	rowHandlersRef.current = {
		handleReprompt,
		handleRegenerate,
		handleGenerateVideo,
		handleDownloadImage,
		handleEditImage,
		handleDeleteImage,
		handleFavoriteToggle,
		setSelectedImage,
		handleVideoAction,
		setSelectedVideo,
		toggleVideoFavorite,
		handleDeleteVideo,
	};
	const imageRowHandlers = useMemo<ImageRowHandlers>(
		() => ({
			openDetail: (item, imageIndex) =>
				rowHandlersRef.current.setSelectedImage({
					generation: item as unknown as ImageGeneration,
					imageIndex,
				}),
			reprompt: (item) => {
				void rowHandlersRef.current.handleReprompt(
					item as unknown as ImageGeneration,
				);
			},
			regenerate: (item) => {
				void rowHandlersRef.current.handleRegenerate(
					item as unknown as ImageGeneration,
				);
			},
			generateVideo: (imageUrl) =>
				rowHandlersRef.current.handleGenerateVideo(imageUrl),
			download: (imageUrl, item, imageIndex) => {
				void rowHandlersRef.current.handleDownloadImage(
					imageUrl,
					item as unknown as ImageGeneration,
					imageIndex,
				);
			},
			edit: (imageUrl) =>
				rowHandlersRef.current.handleEditImage(imageUrl),
			deleteImage: (itemId, imageIndex) =>
				rowHandlersRef.current.handleDeleteImage(itemId, imageIndex),
			toggleFavorite: (itemId, imageIndex, isCurrentlyFavorite) => {
				void rowHandlersRef.current.handleFavoriteToggle(
					itemId,
					imageIndex,
					isCurrentlyFavorite,
				);
			},
		}),
		[],
	);
	const videoRowHandlers = useMemo<VideoRowHandlers>(
		() => ({
			reprompt: (item) =>
				rowHandlersRef.current.handleVideoAction(item, "reprompt"),
			regenerate: (item) =>
				rowHandlersRef.current.handleVideoAction(item, "regenerate"),
			openDetail: (item) => rowHandlersRef.current.setSelectedVideo(item),
			toggleFavorite: (item) => {
				void rowHandlersRef.current.toggleVideoFavorite(item);
			},
			deleteVideo: (item) =>
				rowHandlersRef.current.handleDeleteVideo(item),
		}),
		[],
	);
	const imageRowLabels = useMemo<ImageRowLabels>(
		() => ({
			typeLabel: t("imageSettings.status.aiImage"),
			failedMessage: t("imageSettings.errors.generationFailed"),
			openDetailLabel: tAssets("detail.title"),
			statusLabels: historyStatusLabels,
			actionLabels: imageActionLabels,
		}),
		[t, tAssets, historyStatusLabels, imageActionLabels],
	);
	// 按全量清单查：下拉里隐藏的旧模型（nano-banana-2）的历史记录也要显示名字
	const modelLabel = useCallback(
		(model: string) => allModels.find((m) => m.id === model)?.name || model,
		[allModels],
	);

	// 配置条 + 设置面板，跟着 prompt 走（10/25 老公定模板页也用它，替掉原来钉在 Create 上面的两个胶囊下拉）
	const settingsBar = (
		<GenerationSettings
			trigger={
				<button type="button" className={SETTINGS_BAR_CLASS}>
					{compactSettingsParts.map((part) => (
						<span
							key={part.key}
							className="flex h-5 flex-1 items-center justify-center whitespace-nowrap border-l border-zinc-300/70 px-2 tabular-nums first:border-l-0"
						>
							{part.label}
						</span>
					))}
					<ChevronDownIcon className="ml-1 h-4 w-4 flex-shrink-0 text-zinc-500 transition-transform group-data-[state=open]:rotate-180 lg:-rotate-90 lg:group-data-[state=open]:-rotate-90" />
				</button>
			}
		>
			<div className="space-y-4">
				{!validTemplateFromUrl?.behavior?.hideAspectRatio && (
					<div className="space-y-2">
						<p className="text-xs font-medium text-zinc-700">
							Aspect Ratio
						</p>
						{/* 一行 4 格、图字并排，和分辨率/张数同高（10/19 老公选 C：一行 7 格太瘦高，8 个比例还会单出一个） */}
						<div className="grid grid-cols-4 gap-2">
							{visibleAspectRatios.map((ratio) => {
								const isSelected = size === ratio.value;
								return (
									<button
										type="button"
										key={ratio.value}
										onClick={() => {
											setSize(ratio.value);
										}}
										className={cn(
											"flex h-9 items-center justify-center gap-1.5 rounded-xl text-xs transition-colors",
											isSelected
												? "bg-zinc-950 text-white shadow-sm"
												: "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900",
										)}
									>
										<AspectRatioSwatch
											value={ratio.value}
											size={14}
											className={cn(
												"border bg-transparent",
												isSelected
													? "border-white"
													: "border-zinc-500/70",
											)}
										/>
										<span className="font-medium">
											{ratio.label}
										</span>
									</button>
								);
							})}
						</div>
					</div>
				)}

				{showResolutionSelector && (
					<div className="space-y-2">
						<p className="text-xs font-medium text-zinc-700">
							Resolution
						</p>
						<div className="grid grid-cols-3 gap-2">
							{resolutionOptions.map((res) => {
								const isLocked = lockedResolutions.has(res);
								return (
									<button
										type="button"
										key={res}
										// 刻意不用 disabled：禁用按钮收不到
										// click，就弹不出 paywall
										onClick={() => {
											if (isLocked) {
												setUpgradeModalReason(
													"image-resolution",
												);
												setShowUpgradeModal(true);
												return;
											}
											setResolution(res);
										}}
										className={cn(
											"flex h-9 items-center justify-center gap-1 rounded-xl text-[13px] font-medium transition-colors",
											isLocked
												? "bg-zinc-100 text-zinc-500 opacity-40 hover:opacity-60"
												: resolution === res
													? "bg-zinc-950 text-white shadow-sm"
													: "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900",
										)}
									>
										{isLocked && (
											<LockIcon className="h-3 w-3" />
										)}
										{
											getResolutionLabel(
												selectedModel,
												res,
											).label
										}
									</button>
								);
							})}
						</div>
					</div>
				)}

				{!validTemplateFromUrl?.behavior?.hideOutputCount && (
					<div className="space-y-2">
						<p className="text-xs font-medium text-zinc-700">
							Outputs
						</p>
						<div className="grid grid-cols-4 gap-2">
							{modelSettings.outputOptions.map((num) => {
								const isGuestLocked = !user && num > 1;
								return (
									<button
										type="button"
										key={num}
										disabled={isGuestLocked}
										onClick={() => {
											if (isGuestLocked) {
												return;
											}
											setImageCount(num);
										}}
										className={cn(
											"flex h-9 items-center justify-center rounded-xl text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
											imageCount === num
												? "bg-zinc-950 text-white shadow-sm"
												: "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900",
										)}
									>
										{num}
									</button>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</GenerationSettings>
	);

	return (
		<div
			className={cn(
				"studio-theme flex flex-col bg-background p-1 sm:p-4 gap-3 sm:gap-4",
				// 工作台分栏断点见 globals.css 的 studio-split（比模板页的 lg 早）
				// 手机上不套卡片，内容离屏幕边只留 (saas) main 的 16，照 DeeVid / AIReel（10/21）
				studioLayout
					? "max-sm:p-0 studio-split:flex-row studio-split:items-stretch studio-split:min-h-[560px] studio-split:overflow-hidden studio-split:p-0 studio-split:gap-0 studio-split:h-[calc(100vh-var(--language-banner-height,0px))]"
					: "lg:flex-row lg:items-stretch lg:min-h-[560px] lg:overflow-hidden lg:h-[calc(100vh-5rem)]",
			)}
		>
			{/* Sidebar Panel */}
			<aside
				id="ai-image-generator-left-panel"
				className={cn(
					"group/form w-full flex-shrink-0 flex flex-col min-h-0 bg-card border border-border/15 dark:border-border/10 rounded-xl shadow-sm overflow-hidden max-md:overflow-clip",
					// 右栏至少留 280：窄窗口里拖宽过的左栏不把右栏挤没
					studioLayout
						? "max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none studio-split:self-stretch studio-split:w-[var(--left-panel-w,412px)] studio-split:max-w-[calc(100%-280px)] studio-split:rounded-none studio-split:border-0 studio-split:shadow-none"
						: "lg:self-stretch lg:w-1/2 xl:w-[460px]",
				)}
			>
				<div
					suppressHydrationWarning
					className={cn(
						"p-3 sm:p-6 pb-0 flex-shrink-0",
						// 可拖的左栏边距照 DeeVid 用 16（10/19）
						studioLayout &&
							"max-sm:px-0 max-sm:pt-0 studio-split:px-4 studio-split:pt-4",
						// sm:p-6 会把上面的 pb-0 盖回 24。别的页这 24 落在 tab 栏下面；模板页没 tab 栏，
						// 去掉模板卡后标题下空出 52（10/25 老公指出），只在模板页清掉
						isOnTemplateRoute && "sm:pb-0",
					)}
				>
					{validTemplateFromUrl?.id === "ai-smile-generator" ||
					validTemplateFromUrl?.id === "image-to-emoji-converter" ||
					validTemplateFromUrl?.id === "ai-muscle-generator" ||
					validTemplateFromUrl?.id === "ai-big-butt" ||
					validTemplateFromUrl?.id === "ai-feet-generator" ? (
						<div className="mb-3 sm:mb-4">
							<ImageEffectHeading
								href={getTemplatePath(validTemplateFromUrl.id)}
							/>
						</div>
					) : (
						<h1
							className={`${SECTION_TITLE.className} mb-3 sm:mb-4 !text-xl sm:!text-2xl`}
						>
							{titleOverride
								? titleOverride
								: validTemplateFromUrl
									? tRoot(
											`templates.${validTemplateFromUrl.id}.hero.title`,
											{
												defaultValue: `${validTemplateFromUrl.name} Generator`,
											},
										)
									: t("title")}
						</h1>
					)}

					{/* Tabs。10/23 去掉 Templates tab（模板拆去 /ai-tools 和 /ai-effects）；
					    模板页不出 tab 栏，标题已经说清在用哪个 */}
					{(() => {
						if (isOnTemplateRoute) {
							return null;
						}
						const tabs = [
							{
								value: "image-to-image" as const,
								name: t("tabs.imageToImage"),
							},
							{
								value: "text-to-image" as const,
								name: t("tabs.textToImage"),
							},
						];

						return (
							<nav
								aria-label="Tabs"
								className="-mx-3 px-3 sm:mx-0 sm:px-0"
							>
								<div
									className={
										// 可拖的左栏（分栏起）按文字长短分宽：默认宽时都单行，拖窄了才折行
										// 照 DeeVid 的分段控件：灰轨道 + 选中白块（10/19）
										cn(
											"grid grid-cols-2 gap-1 rounded-xl bg-zinc-100 p-1",
											studioLayout &&
												"studio-split:flex studio-split:gap-0.5 studio-split:[&>button]:flex-auto studio-split:[&>button]:px-1 studio-split:[&>button]:text-[13px]",
										)
									}
								>
									{tabs.map((tab) => (
										<button
											key={tab.value}
											type="button"
											onClick={() =>
												handleTabChange(tab.value)
											}
											aria-current={
												activeTab === tab.value
													? "page"
													: undefined
											}
											className={cn(
												activeTab === tab.value
													? "bg-white text-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
													: "text-zinc-500 hover:text-zinc-800",
												"min-w-0 rounded-lg px-1 sm:px-1.5 py-2 text-[10px] sm:text-sm font-medium leading-snug text-center whitespace-nowrap overflow-hidden transition-colors",
												// 可拖的左栏能窄到 300：放不下时折成两行，不截断（手机和模板页照旧单行截断）
												studioLayout &&
													"studio-split:whitespace-normal studio-split:overflow-visible",
											)}
										>
											<span
												className={cn(
													"inline-flex items-center justify-center gap-0.5 min-w-0",
													studioLayout &&
														"studio-split:flex-wrap",
												)}
											>
												<span
													className={cn(
														"truncate",
														studioLayout &&
															"studio-split:overflow-visible studio-split:whitespace-normal",
													)}
												>
													{tab.name}
												</span>
											</span>
										</button>
									))}
								</div>
							</nav>
						);
					})()}
				</div>

				<div
					ref={formScrollRef}
					translate="no"
					className={cn(
						"notranslate flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar px-2 sm:px-6 pt-1 sm:pt-3 pb-3 sm:pb-6",
						studioLayout && "max-sm:px-0 studio-split:px-4",
					)}
				>
					<div className="space-y-2 sm:space-y-4">
						{/* 模型卡：照 AIReel 放在 tab 下面、上传区上面，和视频页同款（10/24 老公定，推翻 10/19 放进设置弹框的做法 D）。
						    模板页的模型由模板定，不显示 */}
						{activeTab !== "templates" && (
							<div className="shrink-0">
								<ModelPicker
									open={modelMenuOpen}
									onOpenChange={setModelMenuOpen}
									title={t("labels.model")}
									trigger={
										<button
											type="button"
											className="block w-full rounded-xl border border-zinc-200 bg-background px-3 py-1.5 text-left transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 data-[state=open]:bg-zinc-50"
										>
											{/* 和视频页模型卡同款：Model 单独一行靠左，logo 跟模型名同一行，整张卡约 52 高 */}
											<span className="block text-xs leading-4 text-zinc-500">
												{t("labels.model")}
											</span>
											<span className="mt-0.5 flex items-center gap-2">
												<ModelBrandIcon
													brand={
														selectedModelDetails.brand
													}
													className="size-[18px]"
												/>
												<span className="min-w-0 flex-1 truncate text-sm font-medium leading-5 text-zinc-900">
													{selectedModelDetails.name}
												</span>
												<ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-zinc-500 lg:-rotate-90" />
											</span>
										</button>
									}
								>
									<ModelMenuItems
										models={modelList}
										value={selectedModel}
										onSelect={(model, event) => {
											if (isModelGuestLocked(model)) {
												return;
											}
											// 付费专属模型对免费用户也置灰，但保留点击——
											// 灰掉不给出路等于死路一条，点击要能弹升级弹窗。
											if (isModelPaidLocked(model)) {
												// 别让菜单关掉，弹窗要盖在它上面
												event.preventDefault();
												setUpgradeModalReason(
													paidModelPaywallReason(
														model,
													),
												);
												setShowUpgradeModal(true);
												return;
											}
											setSelectedModel(model);
										}}
										getCredits={(model) =>
											creditCostAfterSwitch(
												model,
												purchasesLoading ||
													canRemoveWatermark,
											)
										}
										creditIcon={
											<GemIcon className="size-3 opacity-80" />
										}
										isDisabled={isModelGuestLocked}
										isLocked={isModelPaidLocked}
									/>
								</ModelPicker>
							</div>
						)}
						{/* 模板卡（缩略图 + 名字 + 英文描述）10/25 去掉：不能点也切不了模板，和 H1 重复，描述还没翻译 */}
						{/* Image Upload - Show for templates when template is selected */}
						{activeTab === "templates" && selectedTemplate && (
							<div className="space-y-2">
								<Label className="block text-sm font-medium text-zinc-600">
									{selectedTemplate.behavior?.optionalImage
										? t("imageSettings.upload.labelTemplateOptional")
										: t("imageSettings.upload.labelTemplate", {
												max:
													selectedTemplate.behavior
														?.maxImages ?? 2,
											})}
								</Label>
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									multiple
									className="hidden"
									onChange={(e) => {
										handleTemplateFileSelect(
											e.target.files,
										);
										if (e.target) {
											e.target.value = "";
										}
									}}
								/>
								<div className="grid grid-cols-2 gap-3">
									{inputImages.map((image, index) => (
										<div
											key={index}
											className="relative group rounded-lg overflow-hidden bg-zinc-100 aspect-square"
										>
											<img
												src={image}
												alt={`Upload ${index + 1}`}
												className="w-full h-full object-cover"
											/>
											<UploadThumbnailActions
												onPreview={() =>
													setPreviewImageUrl(image)
												}
												onEdit={() =>
													setEditImageIndex(index)
												}
												onDelete={() =>
													handleRemoveImage(index)
												}
											/>
										</div>
									))}
									{inputImages.length <
										(selectedTemplate?.behavior
											?.maxImages ?? 2) && (
										<button
											type="button"
											data-missing-field="image"
											disabled={isUploading}
											onClick={() => {
												fileInputRef.current?.click();
											}}
											onDragOver={handleDragOver}
											onDragLeave={handleDragLeave}
											onDrop={handleTemplateDrop}
											className={cn(
												"w-full bg-zinc-100 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors group aspect-square",
												isUploading
													? "border-zinc-300 cursor-not-allowed"
													: isDragOver
														? "border-blue-400 bg-blue-50 cursor-pointer"
														: "border-zinc-300 hover:bg-zinc-200 cursor-pointer",
											)}
										>
											{isUploading ? (
												<span className="loading loading-infinity loading-lg text-primary" />
											) : (
												<div className="flex flex-col items-center justify-center space-y-2">
													<div className="text-zinc-500 text-4xl font-light leading-none">
														+
													</div>
													<p className="text-sm text-zinc-600 font-medium">
														{t(
															"imageSettings.upload.clickDrop",
														)}
													</p>
												</div>
											)}
										</button>
									)}
								</div>
							</div>
						)}

						{/* Image Upload */}
						{activeTab === "image-to-image" && (
							<div className="space-y-2">
								<Label className="block text-sm font-medium text-zinc-600">
									{t("imageSettings.upload.label")}{" "}
									<span className="text-zinc-400 font-normal">
										(
										{t("imageSettings.upload.maxImages", {
											max: maxInputImages,
										})}
										)
									</span>
								</Label>
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									multiple
									className="hidden"
									onChange={handleFileInputChange}
								/>
								{uploadedImagePreviews.length === 0 ? (
									<div
										data-missing-field="image"
										onClick={
											!isUploading
												? handleClickUpload
												: undefined
										}
										onKeyDown={(e) => {
											if (
												e.key === "Enter" ||
												e.key === " "
											) {
												e.preventDefault();
												if (!isUploading) {
													handleClickUpload();
												}
											}
										}}
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										onDrop={handleDrop}
										className={cn(
											// 照 DeeVid：灰底 + 1px 细虚线 + 圆角 16（10/19）。上面多了模型卡（约 68 高），加高到 260 的门槛从 850 挪到 890，
											// 可视高度 ≤830（1366×768 这类）降到 150，否则配置条会被挤出屏幕（10/24）
											"w-full border border-dashed rounded-2xl flex flex-col items-center justify-center p-3 sm:p-6 text-center transition-colors group min-h-[140px] sm:min-h-[200px] md:[@media(min-height:890px)]:min-h-[260px] md:[@media(max-height:830px)]:min-h-[150px]",
											isUploading
												? "bg-zinc-100 border-zinc-300 cursor-not-allowed"
												: isDragOver
													? "border-blue-400 bg-blue-50 cursor-pointer"
													: "bg-zinc-100 border-zinc-300 hover:bg-zinc-200/60 hover:border-zinc-400 cursor-pointer",
										)}
									>
										{isUploading ? (
											<>
												<span className="loading loading-infinity loading-lg text-primary" />
												<p className="text-sm text-zinc-500 mt-3">
													{t(
														"imageSettings.upload.uploading",
													)}
												</p>
											</>
										) : (
											<>
												<UploadIcon className="mb-3 size-7 text-zinc-700" />
												<p className="text-sm text-zinc-500">
													{t(
														"imageSettings.upload.clickOrDrop",
													)}
												</p>
												<Button
													type="button"
													variant="outline"
													size="sm"
													// 次要入口，退到上传文案后面：透明底细框灰字；min-h-0 抵掉 Button sm 的 44（10/21 老公选 C）
													className="mt-4 h-7.5 min-h-0 gap-1.5 rounded-full border-zinc-300 bg-transparent px-3.5 text-xs font-medium text-zinc-500 hover:bg-white hover:text-zinc-700"
													onClick={(e) => {
														// The dropzone itself opens the file picker
														e.stopPropagation();
														openAssetDialog();
													}}
												>
													<FolderIcon className="h-3.5 w-3.5" />
													{t(
														"imageSettings.upload.selectAsset",
													)}
												</Button>
											</>
										)}
									</div>
								) : (
									<div className="space-y-3">
										<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
											{uploadedImagePreviews.map(
												(preview, index) => (
													<div
														key={index}
														className={cn(
															"relative group rounded-lg overflow-hidden bg-zinc-100 aspect-square",
															// 光标停在 prompt 里的「Image N」上时描边这张
															activeImageRef ===
																index + 1 &&
																"ring-2 ring-primary ring-offset-2",
														)}
													>
														<img
															src={preview}
															alt={formatImageRef(
																index + 1,
															)}
															className="w-full h-full object-cover"
														/>
														{/* 2 张以上才标编号，单张用户看到的和原来一样（10/21 方案 1：底部文字标签） */}
														{uploadedImagePreviews.length >=
															2 && (
															<span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 to-transparent px-2 pt-4 pb-1.5 text-xs font-semibold text-white">
																{formatImageRef(
																	index + 1,
																)}
															</span>
														)}
														<UploadThumbnailActions
															onPreview={() =>
																setPreviewImageUrl(
																	preview,
																)
															}
															onEdit={() =>
																setEditImageIndex(
																	index,
																)
															}
															onDelete={() =>
																handleRemoveImage(
																	index,
																)
															}
														/>
													</div>
												),
											)}
											{uploadedImagePreviews.length <
												maxInputImages && (
												<div
													onClick={
														!isUploading
															? handleClickUpload
															: undefined
													}
													onKeyDown={(e) => {
														if (
															e.key === "Enter" ||
															e.key === " "
														) {
															e.preventDefault();
															if (!isUploading) {
																handleClickUpload();
															}
														}
													}}
													onDragOver={handleDragOver}
													onDragLeave={
														handleDragLeave
													}
													onDrop={handleDrop}
													className={cn(
														"w-full bg-zinc-100 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors group aspect-square",
														isUploading
															? "border-zinc-300 cursor-not-allowed"
															: isDragOver
																? "border-blue-400 bg-blue-50 cursor-pointer"
																: "border-zinc-300 hover:bg-zinc-200 cursor-pointer",
													)}
												>
													{isUploading ? (
														<span className="loading loading-infinity loading-lg text-primary" />
													) : (
														<div className="flex flex-col items-center justify-center space-y-2">
															<div className="text-zinc-500 text-4xl font-light leading-none">
																+
															</div>
														</div>
													)}
												</div>
											)}
										</div>
										<div className="flex items-center justify-between">
											<p className="text-xs text-zinc-500">
												{t(
													"imageSettings.upload.counter",
													{
														current:
															uploadedImagePreviews.length,
														max: maxInputImages,
													},
												)}
											</p>
											<div className="flex gap-2">
												{uploadedImagePreviews.length <
													maxInputImages && (
													<Button
														type="button"
														variant="outline"
														size="sm"
														onClick={
															openAssetDialog
														}
														// 已传图后的这一排和上传区那颗 My Assets 同一套小胶囊，Clear all 跟着同高（10/21）
														className="h-7.5 min-h-0 gap-1.5 rounded-full border-zinc-300 bg-transparent px-3 text-xs font-medium text-zinc-500 hover:bg-white hover:text-zinc-700"
													>
														<FolderIcon className="h-3.5 w-3.5" />
														{t(
															"imageSettings.upload.selectAsset",
														)}
													</Button>
												)}
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={
														handleClearAllImages
													}
													className="h-7.5 min-h-0 rounded-full border-red-200 bg-transparent px-3 text-xs font-medium text-red-500 hover:bg-red-50 hover:text-red-600"
												>
													{t(
														"imageSettings.upload.clearAll",
													)}
												</Button>
											</div>
										</div>
									</div>
								)}
								<SelectAssetDialog
									open={isAssetDialogOpen}
									onOpenChange={(open) => {
										setIsAssetDialogOpen(open);
										// 选完会先走 onConfirm 再关；没选就关掉的话别把「插编号」留给下一次
										if (!open) {
											browseInsertsRefsRef.current = false;
											promptHandleRef.current?.cancelImageRef();
										}
									}}
									remainingSlots={
										maxInputImages -
										uploadedImagePreviews.length
									}
									existingUrls={
										browseInsertsRefsRef.current
											? uploadedImagePreviews
											: undefined
									}
									onConfirm={handleSelectAssets}
								/>
							</div>
						)}

						{/* Prompt Input - hidden for templates with hidePrompt */}
						{(activeTab === "image-to-image" ||
							activeTab === "text-to-image" ||
							(activeTab === "templates" &&
								!selectedTemplate?.behavior?.hidePrompt)) && (
							<div className="space-y-2">
								{/* Use VirtualStagingInput for virtual-staging-ai template */}
								{activeTab === "templates" &&
								selectedTemplate?.id ===
									"virtual-staging-ai" ? (
									<VirtualStagingInput
										value={prompt}
										onChange={setPrompt}
										disabled={isPending}
									/>
								) : (
									<>
										<Label
											htmlFor="prompt"
											className="block text-sm font-medium text-zinc-500"
										>
											{t("prompt.title")}
										</Label>
										{/* 预设胶囊（肌肉页）：点一下换成这句，用户改过字就都不亮 */}
										{activeTab === "templates" &&
											selectedTemplate?.behavior
												?.promptPresets && (
												<div className="flex flex-wrap gap-2">
													{selectedTemplate.behavior.promptPresets.map(
														(preset) => (
															<button
																key={
																	preset.label
																}
																type="button"
																aria-pressed={
																	prompt ===
																	preset.prompt
																}
																disabled={
																	isPending
																}
																onClick={() => {
																	setPrompt(
																		preset.prompt,
																	);
																	// 右栏样片跟着切到同一句 prompt 的那张（Feet 页 11/01）
																	const example =
																		templateShowcaseItems?.find(
																			(
																				item,
																			) =>
																				item.prompt ===
																				preset.prompt,
																		);
																	if (
																		example
																	) {
																		setSelectedPreviewTemplateId(
																			example.id,
																		);
																	}
																}}
																className="h-8 rounded-full border border-zinc-200 bg-card px-3.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 disabled:opacity-50 aria-pressed:border-primary aria-pressed:bg-primary/10 aria-pressed:text-primary"
															>
																{preset.label}
															</button>
														),
													)}
												</div>
											)}
										<div
											ref={promptBoxRef}
											className="relative"
										>
											<PromptTextarea
												handleRef={promptHandleRef}
												highlightImageCount={
													activeTab ===
													"image-to-image"
														? uploadedImagePreviews.length
														: 0
												}
												onActiveImageRefChange={
													setActiveImageRef
												}
												onAtTrigger={
													canReferenceImages
														? (active) =>
																active
																	? openRefPicker(
																			"typed",
																		)
																	: closeRefPicker()
														: undefined
												}
												onKeyDown={(event) => {
													if (
														refPickerHandleRef.current?.handleKeyDown(
															event,
														)
													) {
														event.preventDefault();
													}
												}}
												value={prompt}
												onChange={setPrompt}
												maxLength={500}
												placeholder={
													activeTab ===
													"image-to-image"
														? t(
																// 2 张以上才教「按编号指图」，单张保持原文案
																uploadedImagePreviews.length >=
																	2
																	? "prompt.placeholderImageRefs"
																	: "prompt.placeholderImageToImage",
															)
														: activeTab ===
																"templates"
															? (selectedTemplate?.promptPlaceholder ??
																t(
																	"prompt.placeholderTemplate",
																))
															: t(
																	"prompt.placeholder",
																)
												}
												// 照 DeeVid：中性灰细边、圆角 16、无阴影、常规字重；视口 ≥970 高才加高到 200（10/19 定在 950：左栏这时要 942 高；10/24 上面加了模型卡，挪到 970，否则 950–969 出滚动条）
												className={cn(
													"w-full bg-card border-zinc-200 rounded-2xl p-3 sm:p-4 text-[15px] sm:text-sm placeholder:text-zinc-400 focus:ring-primary focus:border-primary min-h-[110px] sm:min-h-[120px] md:[@media(min-height:970px)]:min-h-[200px] resize-none leading-relaxed",
													// 底部留出 @ Reference 按钮那一行，字不压到按钮上
													activeTab ===
														"image-to-image" &&
														// 手机字号 15px、三行灰字，110 高会被按钮那一行截掉，加到 140
														"pb-12 sm:pb-12 min-h-[140px]",
													// 有预设胶囊的页（肌肉页）默认句有五行，手机 110 高放不下，右下角字数会压在最后一行上
													activeTab === "templates" &&
														selectedTemplate?.behavior?.promptPresets &&
														"min-h-[180px]",
												)}
											/>
											{canReferenceImages && (
												<>
													<button
														ref={refButtonRef}
														type="button"
														aria-expanded={
															isRefPickerOpen
														}
														onClick={() =>
															isRefPickerOpen
																? closeRefPicker()
																: openRefPicker(
																		"button",
																	)
														}
														// 不抢输入框的焦点：光标留在原处，方向键 / 回车还能直接选图
														onMouseDown={(event) =>
															event.preventDefault()
														}
														className="absolute bottom-2 left-3 inline-flex h-7.5 items-center gap-1 rounded-full bg-zinc-100 pr-3 pl-2.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-200 aria-expanded:bg-primary aria-expanded:text-white sm:left-4"
													>
														<AtSignIcon className="size-3.5" />
														{t("imageRefs.button")}
													</button>
													<ImageRefPicker
														handleRef={
															refPickerHandleRef
														}
														open={isRefPickerOpen}
														onClose={closeRefPicker}
														inputImages={
															uploadedImagePreviews
														}
														maxImages={
															maxInputImages
														}
														isLoggedIn={!!user}
														onPickExisting={
															insertExistingImageRef
														}
														onPickAsset={
															insertAssetImageRef
														}
														onBrowseAll={
															browseAllForRef
														}
														ignoreRef={refButtonRef}
														anchorRef={promptBoxRef}
													/>
												</>
											)}
										</div>
										{missingImageRef !== null && (
											<p className="text-xs text-amber-700">
												{t("imageRefs.missing", {
													n: missingImageRef,
													count: uploadedImagePreviews.length,
												})}
											</p>
										)}
									</>
								)}
							</div>
						)}
						{(activeTab !== "templates" ||
							templateSettingCount > 0) && (
							<div className="grid">
								{/* 配置条跟着 prompt 走、分区显示比例 / 分辨率 / 张数，模型在上面的模型卡里；Create 单独钉底（10/24 和视频页一起改，模板页 10/25 也挪上来） */}
								{settingsBar}
							</div>
						)}
					</div>
				</div>

				<div
					translate="no"
					className={cn(
						"notranslate p-4 sm:p-6 pt-2 bg-card space-y-3 flex-shrink-0",
						studioLayout &&
							"max-sm:px-0 max-sm:bg-background studio-split:px-4 studio-split:pb-4",
						// 手机上 Create 钉在屏幕底部，同视频页（10/25）
						"max-md:sticky max-md:bottom-0 max-md:z-10 max-md:group-has-[textarea:focus]/form:static",
					)}
				>
					{/* Create Button */}
					<Button
						onClick={handleGenerateWithScroll}
						// 生成或图片加载中禁用；缺图/缺字时仍可点击提示或登录。
						disabled={isPending || isUploading}
						className={cn(
							"w-full h-11 text-sm font-semibold bg-blue-500 hover:bg-blue-600 text-white border-blue-500 transition-[background-color,opacity] rounded-full disabled:opacity-70",
							missingInput && "opacity-60",
							missingInput &&
								user &&
								"enabled:cursor-not-allowed hover:bg-blue-500",
						)}
						size="lg"
					>
						{isPending ? (
							<>
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white mr-2" />
								{t("imageSettings.buttons.generating")}
							</>
						) : (
							<>
								{t("imageSettings.buttons.create")}
								{user && (
									<>
										<GemIcon className="h-3.5 w-3.5 ml-2 mx-1 opacity-80" />
										{currentCreditCost}
									</>
								)}
							</>
						)}
					</Button>
				</div>
			</aside>

			{studioLayout && (
				<PanelResizer
					width={leftPanelWidth}
					onCommit={commitLeftPanelWidth}
					label={t("resizePanels")}
				/>
			)}

			{/* Main Content - History Panel */}
			<main
				translate="no"
				className={cn(
					"notranslate flex-1 min-h-0 min-w-0 bg-white dark:bg-card border border-zinc-200/20 dark:border-border/10 rounded-xl shadow-sm flex flex-col relative overflow-hidden",
					// 右栏灰底，历史条目本身是白卡片（照 DeeVid）
					studioLayout &&
						"max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none studio-split:rounded-none studio-split:border-0 studio-split:shadow-none studio-split:bg-zinc-100",
				)}
			>
				{/* Feed tabs. Kept outside the branches below so an empty filter
				    still leaves a way back. Guests have no work of their own, so
				    they get Examples / Templates only. */}
				<div
					className={cn(
						"flex-shrink-0 px-2 pt-2 sm:px-6 sm:pt-6",
						studioLayout && "max-sm:px-0",
					)}
				>
					<div className="max-w-5xl mx-auto">
						<HistoryFeedTabs
							view={activeView}
							onViewChange={setFeedView}
							showHistory={!!user}
							filter={historyFilter}
							onFilterChange={(filter) => {
								setHistoryFilter(filter);
								// 定死在作品：自动选的话，筛到空的那一类会被当成没作品跳回示例
								setFeedView("mine");
							}}
						/>
					</div>
				</div>

				{/* History Panel - Always visible */}
				{activeView === "templates" ? (
					<WorkbenchTemplates
						kind="image"
						className={cn(
							"px-2 pt-4 sm:px-6",
							studioLayout && "max-sm:px-0",
						)}
					/>
				) : !user &&
					!templateShowcaseItems?.length &&
					templateIdFromUrl &&
					validTemplateFromUrl?.behavior?.showCustomPreview ? (
					<TemplatePreview templateId={templateIdFromUrl} />
				) : activeView === "examples" ? (
					/* 样片和模板条是一组，整组竖向居中。之前模板条钉在底边，高屏上
					   中间空出一大截（10/22）。my-auto：放不下时从顶上开始滚，不裁顶 */
					<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
						<div className="my-auto">
							<HistoryPreviewShowcase
								containImages={
									Boolean(
										validTemplateFromUrl?.behavior
											?.hidePrompt,
									) ||
									validTemplateFromUrl?.id ===
										"virtual-staging-ai"
								}
								items={templateShowcaseItems}
								selectedTemplateId={selectedPreviewTemplateId}
								onTemplateSelect={setSelectedPreviewTemplateId}
							/>
							<TemplateScrollBar
								items={templateShowcaseItems}
								onTemplateSelect={setSelectedPreviewTemplateId}
								selectedTemplateId={selectedPreviewTemplateId}
								onCreateClick={handlePreviewTemplateCreate}
							/>
						</div>
					</div>
				) : !isLoading &&
					!isFetchingNextPage &&
					historyItems.length === 0 ? (
					/* 某个 tab 空不代表用户没记录，退回示例区会让人以为记录丢了 */
					<div className="flex flex-1 items-center justify-center px-4 pb-6">
						<p className="text-muted-foreground">
							{tAssets("emptyFiltered")}
						</p>
					</div>
				) : (
					<div
						ref={historyScrollRef}
						className="flex-1 overflow-y-auto overscroll-contain py-2 sm:p-6 custom-scrollbar touch-pan-y"
					>
						<div className="max-w-5xl mx-auto pb-6 sm:pb-10">
							<div className="space-y-4 sm:space-y-6">
								{/* Initial Loading State - Just Dots */}
								{isLoading ? (
									<div className="flex flex-col items-center justify-center min-h-[320px] text-center px-4">
										<span className="loading loading-dots loading-lg text-blue-500" />
									</div>
								) : (
									<>
										{historyEntries.map((entry) => {
											if (entry.kind === "videos") {
												return (
													<VideoHistoryRow
														key={entry.items[0].id}
														items={entry.items}
														handlers={
															videoRowHandlers
														}
														statusLabels={
															historyStatusLabels
														}
														actionLabels={
															imageActionLabels
														}
														favoriteKey={entry.items
															.map((clip) =>
																isVideoFavorite(
																	clip,
																)
																	? "1"
																	: "0",
															)
															.join("")}
													/>
												);
											}
											const item =
												entry.item as unknown as HistoryFeedItem;
											return (
												<ImageHistoryRow
													key={item.id}
													item={item}
													handlers={imageRowHandlers}
													labels={imageRowLabels}
													modelLabel={modelLabel}
													isRegenerating={
														regeneratingItemId ===
														item.id
													}
													isRegenerateDisabled={
														regeneratingItemId ===
															item.id ||
														generateImageMutation.isPending
													}
													favoriteKey={(
														item.imageUrls ?? []
													)
														.map((_, i) =>
															isImageFavorite(
																item,
																i,
															)
																? "1"
																: "0",
														)
														.join("")}
												/>
											);
										})}

										{isFetchingNextPage && (
											<div className="flex flex-col items-center justify-center py-10 w-full gap-2 opacity-80">
												<span className="loading loading-dots loading-lg text-blue-500" />
											</div>
										)}
										{!isFetchingNextPage && hasNextPage && (
											<div
												ref={inViewRef}
												className="h-4"
											/>
										)}
									</>
								)}
							</div>
						</div>
					</div>
				)}
			</main>
			<CreditsPaywallModal
				open={showUpgradeModal}
				onOpenChange={(open) => {
					setShowUpgradeModal(open);
					if (!open) {
						setUpgradeModalReason(null);
						// 一并清掉，否则下次因积分不足弹窗会沿用上次的模型文案
						setPendingUpgradeReason(null);
					}
				}}
				reason={upgradeModalReason}
			/>
			<UploadPreviewDialog
				imageUrl={previewImageUrl}
				onClose={() => setPreviewImageUrl(null)}
			/>
			<EditUploadedImageDialog
				open={editImageIndex !== null}
				imageUrl={
					editImageIndex !== null
						? (uploadedImagePreviews[editImageIndex] ?? null)
						: null
				}
				onOpenChange={(open) => {
					if (!open) {
						setEditImageIndex(null);
					}
				}}
				onConfirm={handleEditConfirm}
			/>
			{selectedVideo && (
				<VideoDetailModal
					isOpen={!!selectedVideo}
					onClose={() => setSelectedVideo(null)}
					video={selectedVideo}
					onReprompt={() =>
						handleVideoAction(selectedVideo, "reprompt")
					}
					onRegenerate={() =>
						handleVideoAction(selectedVideo, "regenerate")
					}
					isFavorite={isVideoFavorite(selectedVideo)}
					onToggleFavorite={() => toggleVideoFavorite(selectedVideo)}
				/>
			)}
			{selectedImage && (
				<ImageDetailModal
					isOpen={!!selectedImage}
					onClose={() => setSelectedImage(null)}
					generation={selectedImage.generation}
					initialImageIndex={selectedImage.imageIndex}
					onReprompt={() => handleReprompt(selectedImage.generation)}
					onRegenerate={() =>
						handleRegenerate(selectedImage.generation)
					}
					onGenerateVideo={handleGenerateVideo}
					isImageFavorite={(imageIndex) =>
						isImageFavorite(selectedImage.generation, imageIndex)
					}
					onToggleFavorite={(imageIndex) =>
						handleFavoriteToggle(
							selectedImage.generation.id,
							imageIndex,
							isImageFavorite(
								selectedImage.generation,
								imageIndex,
							),
						)
					}
					onDelete={(imageIndex) =>
						handleDeleteImage(
							selectedImage.generation.id,
							imageIndex,
							() => setSelectedImage(null),
						)
					}
				/>
			)}
		</div>
	);
}
